import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { parseProductUrl } from '../data/products';
import {
  filterCatalogProducts,
  isCatalogProductForDisplay,
  isDemoProduct,
} from '../utils/isDemoProduct';
import { normalizeProductPricesForDisplay } from '../utils/productPricing';
import {
  buildProductShareMeta,
  copyProductShareToClipboard,
  openFacebookProductShare,
} from '../utils/productShareText';
import { useBag } from '../context/BagContext';
import { useTracking } from '../context/TrackingContext';
import { useAuth } from '../context/AuthContext';
import { useLikedItems } from '../context/WishlistContext';
import { useOrders } from '../context/OrderContext';
import { useProducts } from '../context/ProductsContext';
import {
  productService,
  cartService,
  settingsService,
  categoryService,
  creatorStorefrontService,
} from '../services/api';
import {
  getWhatsAppOrderPhoneDigits,
  buildWhatsAppUrl,
  persistContactChannelsLocal,
} from '../utils/whatsappContact';
import reviewService from '../services/reviewService';
import ProductCard from '../components/ProductCard';
import FeaturedProductsCarousel from '../components/FeaturedProductsCarousel';
import PdpMobileImageLightbox from '../components/PdpMobileImageLightbox';
import SizeChart from '../components/SizeChart';
import ReviewCard from '../components/ReviewCard';
import ReviewForm from '../components/ReviewForm';
import SiteLoading from '../components/SiteLoading';
import { getCategorySlug } from '../utils/categoryUtils';
import { getCreatorDisplayName } from '../admin/utils/authUtils';
import {
  getCreatorShopLinkPath,
  mergePopulatedCreatedBy,
  normalizeCreatorSlugInput,
  rememberCreatorSlug,
} from '../utils/creatorSlug';
import { isDefaultColorLabel, isDefaultColorPlaceholder } from '../utils/productColorLabels';
import { normalizeHexColor, getColorDisplayName } from '../utils/productColorDisplay';
import {
  hasRenderableRichContent,
  mergeDescriptionRichSources,
  stripEmbedFromDescriptionForDisplay,
} from '../utils/productDescriptionRich';
import { isLikelyHtmlDescription } from '../utils/productDescriptionHtml';
import ProductDescriptionRich from '../components/ProductDescriptionRich';
import ProductDescriptionHtml from '../components/ProductDescriptionHtml';
import {
  getProductImageIndexForColor,
  getPrimaryProductImageIndex,
  getProductImageUrl,
  getLinkedColorFromImage,
  normalizeProductImagesForStorefront,
} from '../utils/productImages';
import { getStorefrontSizeDisplay } from '../utils/kidsClothingSizeDisplay';
import { buildProductCategoryLinks } from '../utils/productCategoryBreadcrumb';
import {
  applyProductOpenGraphMeta,
  applyDocumentBranding,
  readSiteSettingsFromStorage,
} from '../utils/siteBranding';
import { extractProductTagLabel, tagToUrlSlug } from '../utils/productTags';
import { takePrefetchedPdpProduct } from '../utils/pdpPrefetchCache';
import { fetchPdpYouMayAlsoLikeProducts } from '../utils/pdpYouMayAlsoLike';
import './ProductPage.css';

function buildMaterialTagNavItems(tags) {
  if (!Array.isArray(tags) || !tags.length) return [];
  return tags.map((tag, index) => {
    const tagValue = extractProductTagLabel(tag);
    if (!tagValue) return null;
    return { tagValue, tagSlug: tagToUrlSlug(tagValue), index };
  }).filter(Boolean);
}

function navigateToProductTagListing(navigate, tagValue) {
  const slug = tagToUrlSlug(tagValue);
  navigate(`/tag/${encodeURIComponent(slug)}`, { state: { tagLabel: tagValue } });
}

const DEFAULT_SIZE = 'Default size';

/** You May Also Like: 8 visible per view; desktop loads a larger pool and auto-advances every 10s. */
const YMAL_PAGE_SIZE = 8;
const YMAL_POOL_MAX = 32;
const YMAL_DESKTOP_ROTATE_MS = 10000;

function ymalProductKey(p) {
  if (!p || typeof p !== 'object') return '';
  return String(p.id ?? p._id ?? '');
}

/** Dedupe YMAL pool by product id (preserves first occurrence order). */
function dedupeYmalProducts(arr) {
  const seen = new Set();
  return arr.filter((p) => {
    const k = ymalProductKey(p);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Ensure ProductCard + YMAL get stable `id` and image/url from API-shaped rows. */
function normalizeYmalProductFromApi(p) {
  if (!p || typeof p !== 'object' || !isCatalogProductForDisplay(p)) return null;
  const id = p.id ?? p._id;
  if (id == null || String(id).trim() === '') return null;
  const cat = p.category;
  const categoryString =
    typeof cat === 'string' ? cat : cat?.name || cat?.slug || cat?.fullPath || 'general';
  const sub = p.subcategory;
  const subString = typeof sub === 'string' ? sub : sub?.name || '';
  let image = p.image;
  if (image == null || image === '') {
    if (Array.isArray(p.images) && p.images.length > 0) {
      try {
        image = getProductImageUrl(p.images[0]);
      } catch {
        image = null;
      }
    }
  }
  const prices = normalizeProductPricesForDisplay(p);
  return {
    ...p,
    id: String(id),
    name: p.name || 'Product',
    price: prices.price,
    originalPrice: prices.originalPrice,
    salePrice: prices.salePrice,
    image,
    url: p.url || `product/${String(id)}`,
    category: categoryString,
    subcategory: subString,
    isNew: !!p.isNew,
    isOnSale: prices.isOnSale,
  };
}

function findCatalogProductInList(allProducts, { id: routeId, productUrl: routeUrl }) {
  if (!Array.isArray(allProducts) || allProducts.length === 0) return null;
  if (routeId) {
    const hit = allProducts.find((p) => {
      if (isDemoProduct(p)) return false;
      const pid = p.id ?? p._id;
      return pid != null && String(pid) === String(routeId);
    });
    if (hit) return hit;
  }
  if (routeUrl) {
    const hit = allProducts.find((p) => !isDemoProduct(p) && p.url === routeUrl);
    if (hit) return hit;
  }
  return null;
}

/** Sync map API product → PDP state (first paint + loadProduct). */
function mapProductApiToPdpPage(productData, allProducts) {
  if (!productData || isDemoProduct(productData)) return null;

  const productId = productData.id || productData._id || productData.productId;
  const canonicalPid = productId;
  let productColors = productData.colors || [];
  let productSizes = productData.sizes || [];

  productColors = productColors
    .map((color) => {
      if (typeof color === 'string') return color;
      if (color && typeof color === 'object') {
        return color.name || color.value || color.color || String(color);
      }
      return String(color);
    })
    .filter((color) => color && color.trim());

  productSizes = productSizes
    .map((size) => {
      if (typeof size === 'string') return size;
      if (size && typeof size === 'object') {
        return size.name || size.value || size.size || String(size);
      }
      return String(size);
    })
    .filter((size) => size && size.trim());

  if (productColors.length === 0 && productData.stock) {
    productColors = Object.keys(productData.stock);
  }
  if (productSizes.length === 0 && productData.stock && productColors.length > 0) {
    const firstColor = productColors[0];
    if (productData.stock[firstColor] && typeof productData.stock[firstColor] === 'object') {
      productSizes = Object.keys(productData.stock[firstColor]);
    }
  }

  let createdBy = mergePopulatedCreatedBy(productData.createdBy, null);
  const creatorId =
    typeof createdBy === 'string' ? createdBy : createdBy?._id || createdBy?.id;
  const hasCreatorName =
    createdBy &&
    typeof createdBy === 'object' &&
    (createdBy.name || createdBy.email);
  if (creatorId && !hasCreatorName) {
    const productUrlMatch = productData.url;
    const matchFromList = allProducts?.find((p) => {
      if (isDemoProduct(p)) return false;
      const pid = p.id || p._id;
      const sameId = pid && productId && pid.toString() === productId.toString();
      const sameUrl = productUrlMatch && p.url === productUrlMatch;
      return sameId || sameUrl;
    });
    if (
      matchFromList?.createdBy &&
      typeof matchFromList.createdBy === 'object' &&
      (matchFromList.createdBy.name || matchFromList.createdBy.email)
    ) {
      createdBy = mergePopulatedCreatedBy(matchFromList.createdBy, createdBy);
    } else {
      createdBy = mergePopulatedCreatedBy(
        { _id: creatorId, name: 'Sheen Wear', role: 'team' },
        createdBy
      );
    }
  }

  if (creatorId && createdBy && typeof createdBy === 'object') {
    const slugOnProduct = normalizeCreatorSlugInput(createdBy.creatorSlug);
    if (slugOnProduct) rememberCreatorSlug(creatorId, slugOnProduct);
  }

  const rawDescription = productData.description || '';
  const mergedRichBlocks = mergeDescriptionRichSources(productData.descriptionRich, rawDescription);
  const descriptionPlain =
    stripEmbedFromDescriptionForDisplay(rawDescription) || rawDescription.trim();
  const rawImagesFromApi = Array.isArray(productData.images) ? productData.images : [];
  const firstImg = rawImagesFromApi[0];
  const safeProductData = {
    id: productData.id || productData._id || Math.random(),
    _id: productData._id || productData.id,
    productId: productData.productId || productData.id || productData._id,
    name: productData.name || 'Product',
    brand: productData.brand || '',
    category: productData.category || 'general',
    mainCategory: productData.mainCategory || 'general',
    subcategory: productData.subcategory || '',
    childrenCategory: productData.childrenCategory || '',
    tags: Array.isArray(productData.tags) ? productData.tags : [],
    ...(() => {
      const prices = normalizeProductPricesForDisplay(productData);
      return {
        price: prices.price,
        originalPrice: prices.originalPrice,
        salePrice: prices.salePrice,
        isOnSale: prices.isOnSale,
      };
    })(),
    image: firstImg ? getProductImageUrl(firstImg) : null,
    images: rawImagesFromApi,
    primaryImageIndex:
      productData.primaryImageIndex ?? productData.primary_image_index ?? undefined,
    description: descriptionPlain || 'Product description',
    descriptionRich: mergedRichBlocks,
    isNew: productData.isNew || false,
    sizes: productSizes,
    colors: productColors,
    fabric: productData.fabric || '',
    material: productData.material || '',
    care: productData.care || '',
    stock: productData.stock || {},
    totalStock: productData.totalStock ?? productData.stockTotal ?? null,
    url: productData.url || `product/${productData.id || productData._id}`,
    createdAt: productData.createdAt || new Date(),
    updatedAt: productData.updatedAt || new Date(),
    createdBy,
  };

  if (!safeProductData.images || safeProductData.images.length === 0) {
    safeProductData.images = safeProductData.image ? [safeProductData.image] : [];
  }

  const finalColors = safeProductData.colors || [];
  const finalSizes = safeProductData.sizes || [];
  const hasNoVariants = finalColors.length === 0 && finalSizes.length === 0;
  const isSingleSkuQtyOnly = isSingleSkuNoPicker(finalColors, finalSizes);

  let variantUi;
  if (isSingleSkuQtyOnly) {
    const colorKey = finalColors[0];
    const sizeKey = finalSizes[0];
    const cell = safeProductData.stock?.[colorKey]?.[sizeKey];
    const cellN = parseInt(cell, 10);
    const total = resolveTotalStock(safeProductData, productData);
    variantUi = {
      selectedColor: colorKey,
      selectedSize: sizeKey,
      currentStock: !Number.isNaN(cellN) && cellN >= 0 ? cellN : total,
      availableColors: [],
      availableSizes: [],
    };
  } else if (hasNoVariants) {
    const colorKey = finalColors[0] ?? 'default';
    const sizeKey = finalSizes[0] ?? 'default';
    const cell = safeProductData.stock?.[colorKey]?.[sizeKey];
    const cellN = parseInt(cell, 10);
    const total = resolveTotalStock(safeProductData, productData);
    variantUi = {
      selectedColor: colorKey,
      selectedSize: sizeKey,
      currentStock: !Number.isNaN(cellN) && cellN >= 0 ? cellN : total,
      availableColors: [],
      availableSizes: [],
    };
  } else {
    const selectableColorsOnLoad = filterSelectableColors(finalColors);
    const selectableSizesOnLoad = filterSelectableSizes(finalSizes);
    variantUi = {
      selectedColor:
        selectableColorsOnLoad.length === 0 && finalColors.length > 0
          ? resolveDefaultColorFromList(finalColors)
          : '',
      selectedSize:
        selectableSizesOnLoad.length === 0 && finalSizes.length > 0
          ? resolveDefaultSizeFromList(finalSizes)
          : '',
      currentStock: 0,
      availableColors: finalColors,
      availableSizes: finalSizes,
    };
  }

  return {
    safeProductData,
    variantUi,
    enrichPid: canonicalPid,
    enrichSource: productData,
    enrichColors: productColors,
  };
}

function applyPdpVariantUi(variantUi, setters) {
  if (!variantUi) return;
  setters.setSelectedColor(variantUi.selectedColor ?? '');
  setters.setSelectedSize(variantUi.selectedSize ?? '');
  setters.setCurrentStock(variantUi.currentStock ?? 0);
  setters.setAvailableColors(variantUi.availableColors ?? []);
  setters.setAvailableSizes(variantUi.availableSizes ?? []);
  setters.setValidationError('');
  setters.setSizeStocks({});
}

function runPdpBackgroundEnrich(enrichPid, productData, productColors, setProduct) {
  if (!enrichPid) return;
  void (async () => {
    let enriched = { ...productData };
    let enrichedColors = [...(productColors || [])];
    const tasks = [];
    if (needsCanonicalProductMerge(enriched)) {
      tasks.push(
        productService
          .getProduct(enrichPid)
          .then((fullRes) => ({ kind: 'canonical', fullRes }))
          .catch(() => ({ kind: 'canonical', fullRes: null }))
      );
    }
    if (!enrichedColors.length) {
      tasks.push(
        productService
          .getProductColors(enrichPid)
          .then((colorsResponse) => ({ kind: 'colors', colorsResponse }))
          .catch(() => ({ kind: 'colors', colorsResponse: null }))
      );
    }
    if (!tasks.length) return;
    const parallelResults = await Promise.all(tasks);
    for (const row of parallelResults) {
      if (row.kind === 'canonical' && row.fullRes) {
        enriched = mergeCanonicalProductResponse(enriched, row.fullRes);
      }
      if (row.kind === 'colors' && row.colorsResponse?.success && row.colorsResponse?.data?.colors) {
        enrichedColors = row.colorsResponse.data.colors;
      }
    }
    enriched = {
      ...enriched,
      images: normalizeProductImagesForStorefront(enriched),
    };
    enrichedColors = enrichedColors
      .map((color) => {
        if (typeof color === 'string') return color;
        if (color && typeof color === 'object') {
          return color.name || color.value || color.color || String(color);
        }
        return String(color);
      })
      .filter((color) => color && color.trim());
    if (enrichedColors.length === 0 && enriched.stock) {
      enrichedColors = Object.keys(enriched.stock);
    }
    setProduct((prev) => {
      if (!prev) return prev;
      const prevId = String(prev.id ?? prev._id ?? '');
      if (prevId !== String(enrichPid)) return prev;
      return {
        ...prev,
        images: enriched.images ?? prev.images,
        primaryImageIndex: enriched.primaryImageIndex ?? prev.primaryImageIndex,
        linkedColors: enriched.linkedColors ?? prev.linkedColors,
        imagesMeta: enriched.imagesMeta ?? prev.imagesMeta,
        stock: enriched.stock ?? prev.stock,
        totalStock: enriched.totalStock ?? prev.totalStock,
        colors: enrichedColors.length ? enrichedColors : prev.colors,
      };
    });
  })();
}

function needsCanonicalProductMerge(productData) {
  if (!productData) return false;
  const imgs = productData.images;
  const hasGallery =
    Array.isArray(imgs) &&
    imgs.length > 0 &&
    imgs.some((img) => {
      if (!img) return false;
      if (typeof img === 'string') return img.trim().length > 0;
      return Boolean(img.url || img.src || img.path);
    });
  const hasLinked =
    (Array.isArray(productData.linkedColors) && productData.linkedColors.length > 0) ||
    (Array.isArray(productData.linked_colors) && productData.linked_colors.length > 0);
  const hasMeta = productData.imagesMeta != null || productData.images_meta != null;
  const needStock =
    aggregateNestedStock(productData.stock || {}) === 0 &&
    productData.totalStock == null &&
    productData.stockTotal == null;
  return !hasGallery || needStock || (!hasLinked && !hasMeta);
}

function mergeCanonicalProductResponse(productData, fullRes) {
  const merged = fullRes?.product;
  if (!merged || typeof merged !== 'object') return productData;
  const needStock =
    aggregateNestedStock(productData.stock || {}) === 0 &&
    productData.totalStock == null &&
    productData.stockTotal == null;
  if (Array.isArray(merged.images) && merged.images.length > 0) {
    return {
      ...productData,
      images: merged.images,
      primaryImageIndex: merged.primaryImageIndex ?? productData.primaryImageIndex,
      imagesMeta: merged.imagesMeta ?? merged.images_meta ?? productData.imagesMeta,
      linkedColors: merged.linkedColors ?? merged.linked_colors ?? productData.linkedColors,
      ...(needStock && (merged.stock || merged.totalStock != null || merged.stockTotal != null)
        ? {
            stock: merged.stock ?? productData.stock,
            totalStock: merged.totalStock ?? productData.totalStock,
            stockTotal: merged.stockTotal ?? productData.stockTotal,
          }
        : {}),
    };
  }
  const mLc = merged.linkedColors ?? merged.linked_colors;
  const mMeta = merged.imagesMeta ?? merged.images_meta;
  if (
    (Array.isArray(mLc) && mLc.length > 0) ||
    (mMeta != null && (Array.isArray(mMeta) || typeof mMeta === 'object'))
  ) {
    return {
      ...productData,
      linkedColors: mLc ?? productData.linkedColors,
      imagesMeta: mMeta ?? productData.imagesMeta,
      ...(needStock && (merged.stock || merged.totalStock != null || merged.stockTotal != null)
        ? {
            stock: merged.stock ?? productData.stock,
            totalStock: merged.totalStock ?? productData.totalStock,
            stockTotal: merged.stockTotal ?? productData.stockTotal,
          }
        : {}),
    };
  }
  if (needStock && (merged.stock || merged.totalStock != null || merged.stockTotal != null)) {
    return {
      ...productData,
      stock: merged.stock ?? productData.stock,
      totalStock: merged.totalStock ?? productData.totalStock,
      stockTotal: merged.stockTotal ?? productData.stockTotal,
    };
  }
  return productData;
}

/** "You May Also Like" — same category tree as current product (child → sub → main). */
async function loadYmalForProduct(productData) {
  const pool = await fetchPdpYouMayAlsoLikeProducts(productData, { poolMax: YMAL_POOL_MAX });
  const related = dedupeYmalProducts(
    pool.map(normalizeYmalProductFromApi).filter(Boolean)
  ).slice(0, YMAL_POOL_MAX);
  return filterCatalogProducts(related);
}

/** Desktop: always `pageSize` cards when pool has enough; wrap with modulo so the last "page" is not short. */
function sliceYmalDesktopWindow(items, pageIndex, pageSize) {
  const n = items.length;
  if (n === 0) return [];
  if (n < pageSize) return items.slice();
  const start = (pageIndex * pageSize) % n;
  const out = [];
  for (let i = 0; i < pageSize; i += 1) {
    out.push(items[(start + i) % n]);
  }
  return out;
}

/** Heart icon for gallery wishlist — inline SVG so in-app webviews still show it if Font Awesome CDN is blocked. */
function ProductWishlistHeartIcon({ filled }) {
  return (
    <svg
      className="product-wishlist-heart-icon"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden
      focusable="false"
    >
      <path
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
      />
    </svg>
  );
}

/** Dedupe rapid double POST from React Strict Mode / double effect (same product). */
let __productViewPostLastId = null;
let __productViewPostLastAt = 0;
function shouldSkipDuplicateProductViewPost(productId) {
  const now = Date.now();
  if (
    __productViewPostLastId === productId &&
    now - __productViewPostLastAt < 900
  ) {
    return true;
  }
  __productViewPostLastId = productId;
  __productViewPostLastAt = now;
  return false;
}

/** Paths served by static routes; must not be handled as /:productUrl (SEO slug). */
const RESERVED_STATIC_PATHS = {
  about: '/about',
  contact: '/contact',
  'return-policy': '/return-policy',
  'privacy-policy': '/privacy-policy',
  'payment-methods': '/payment-methods',
};

/** Sum numeric leaves in nested stock object (color → size → qty). */
function aggregateNestedStock(stock) {
  if (stock == null) return 0;
  if (typeof stock === 'number') return Math.max(0, parseInt(stock, 10) || 0);
  if (typeof stock !== 'object') return 0;
  let sum = 0;
  const walk = (node) => {
    if (typeof node === 'number') sum += parseInt(node, 10) || 0;
    else if (node && typeof node === 'object') Object.values(node).forEach(walk);
  };
  walk(stock);
  return sum;
}

/** Prefer explicit totalStock from API; otherwise sum nested stock. */
function resolveTotalStock(product, raw = {}) {
  const direct =
    raw.totalStock ??
    raw.stockTotal ??
    product?.totalStock ??
    product?.stockTotal;
  if (direct != null && direct !== '') {
    const n = parseInt(direct, 10);
    if (!Number.isNaN(n)) return Math.max(0, n);
  }
  return aggregateNestedStock(product?.stock);
}

/** Backend often sends size key `default` while UI used `Default size`; color may be `As image` or `default`. */
function isDefaultSizePlaceholder(size) {
  if (size == null) return false;
  const s = String(size).trim();
  if (s === DEFAULT_SIZE) return true;
  return s.toLowerCase() === 'default';
}

/** Single purchasable SKU with no real variant UI: As image + default, or default/default, etc. */
function isSingleSkuNoPicker(colors, sizes) {
  if (!Array.isArray(colors) || !Array.isArray(sizes)) return false;
  if (colors.length !== 1 || sizes.length !== 1) return false;
  const c = String(colors[0]).trim();
  const cl = c.toLowerCase();
  const colorOk = isDefaultColorLabel(c) || cl === 'default';
  const sizeOk = isDefaultSizePlaceholder(sizes[0]);
  return colorOk && sizeOk;
}

function getSizeLabel(sizeLike) {
  if (typeof sizeLike === 'string') return sizeLike;
  if (sizeLike && typeof sizeLike === 'object') {
    return (
      sizeLike.size ||
      sizeLike.name ||
      sizeLike.value ||
      String(sizeLike)
    );
  }
  return String(sizeLike ?? '');
}

/** Real storefront sizes only — hide `Default size` / `default` unless other sizes exist. */
function filterSelectableSizes(sizes) {
  if (!Array.isArray(sizes)) return [];
  return sizes.filter((s) => !isDefaultSizePlaceholder(getSizeLabel(s)));
}

function productHasSelectableSizes(sizes) {
  return filterSelectableSizes(sizes).length > 0;
}

function resolveDefaultSizeFromList(sizes) {
  if (!Array.isArray(sizes) || sizes.length === 0) return 'default';
  for (const s of sizes) {
    const label = getSizeLabel(s);
    if (isDefaultSizePlaceholder(label)) return label;
  }
  return getSizeLabel(sizes[0]);
}

function getSizeListForProductPicker(product, availableSizes) {
  if (Array.isArray(availableSizes) && availableSizes.length > 0) return availableSizes;
  return product?.sizes || [];
}

function getColorNameFromEntry(color) {
  if (typeof color === 'string') return color.trim();
  if (color && typeof color === 'object') {
    return String(color.name || color.value || color.color || color).trim();
  }
  return String(color ?? '').trim();
}

function filterSelectableColors(colors) {
  if (!Array.isArray(colors)) return [];
  return colors.filter((c) => !isDefaultColorPlaceholder(c));
}

function productHasSelectableColors(colors) {
  return filterSelectableColors(colors).length > 0;
}

function resolveDefaultColorFromList(colors) {
  if (!Array.isArray(colors) || colors.length === 0) return 'default';
  for (const c of colors) {
    if (isDefaultColorPlaceholder(c)) return getColorNameFromEntry(c);
  }
  return getColorNameFromEntry(colors[0]);
}

function getColorListForProductPicker(product, availableColors) {
  if (Array.isArray(availableColors) && availableColors.length > 0) return availableColors;
  return product?.colors || [];
}

function readSizeStockFromMap(stockMap, sizeLike) {
  if (!stockMap || typeof stockMap !== 'object') return null;
  const raw = getSizeLabel(sizeLike).trim();
  if (!raw) return null;
  if (stockMap[raw] !== undefined) return parseInt(stockMap[raw], 10) || 0;
  const upper = raw.toUpperCase();
  if (stockMap[upper] !== undefined) return parseInt(stockMap[upper], 10) || 0;
  const lower = raw.toLowerCase();
  if (stockMap[lower] !== undefined) return parseInt(stockMap[lower], 10) || 0;
  const foundKey = Object.keys(stockMap).find((k) => String(k).trim().toLowerCase() === lower);
  if (foundKey) return parseInt(stockMap[foundKey], 10) || 0;
  return null;
}

function normalizeSizesApiPayload(data) {
  const rawSizes = Array.isArray(data?.sizes) ? data.sizes : [];
  const stockObj = data?.stock && typeof data.stock === 'object' ? data.stock : {};
  const seen = new Set();
  const labels = [];
  const stockMap = {};

  rawSizes.forEach((entry) => {
    const label = getSizeLabel(entry).trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
    const fromStockObj = readSizeStockFromMap(stockObj, label);
    if (fromStockObj !== null) {
      stockMap[label] = fromStockObj;
    } else if (entry && typeof entry === 'object') {
      const embedded = entry.stock ?? entry.quantity ?? entry.qty ?? entry.availableStock;
      stockMap[label] = parseInt(embedded, 10) || 0;
    } else {
      stockMap[label] = 0;
    }
  });

  Object.keys(stockObj).forEach((k) => {
    const label = getSizeLabel(k).trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      labels.push(label);
    }
    const qty = readSizeStockFromMap(stockObj, label);
    stockMap[label] = qty ?? stockMap[label] ?? 0;
  });

  return { labels, stockMap };
}

const ProductPage = ({ prefetchedProduct = null }) => {
  const { id, productUrl } = useParams();
  const navigate = useNavigate();
  const { addToBag: addToGuestBag } = useBag();
  const { getRecentVisits, trackVisit } = useTracking();
  const { user, isAuthenticated } = useAuth();
  const { addToLikedItems, removeFromLikedItems, isLiked } = useLikedItems();
  const { getOrdersByUserId } = useOrders();
  const { products: allProducts } = useProducts();

  const pdpMountSeedRef = useRef(undefined);
  if (pdpMountSeedRef.current === undefined) {
    if (prefetchedProduct && !isDemoProduct(prefetchedProduct)) {
      pdpMountSeedRef.current = mapProductApiToPdpPage(
        {
          ...prefetchedProduct,
          images: normalizeProductImagesForStorefront(prefetchedProduct),
        },
        []
      );
    } else {
      pdpMountSeedRef.current = null;
    }
  }
  const pdpMountSeed = pdpMountSeedRef.current;
  const pdpEnrichStartedRef = useRef(null);
  
  const [product, setProduct] = useState(() => pdpMountSeed?.safeProductData ?? null);
  const [allCategories, setAllCategories] = useState([]);
  const productRef = useRef(null);
  useEffect(() => {
    productRef.current = product;
  }, [product]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await categoryService.getCategories();
        if (cancelled) return;
        const rows = Array.isArray(res.categories)
          ? res.categories
          : Array.isArray(res.categories?.data)
            ? res.categories.data
            : Array.isArray(res.data)
              ? res.data
              : [];
        setAllCategories(rows);
      } catch {
        if (!cancelled) setAllCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const productCategoryLinks = useMemo(
    () => buildProductCategoryLinks(product, allCategories),
    [product, allCategories]
  );

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(() => !pdpMountSeed);
  const [addingToBag, setAddingToBag] = useState(false);
  /** After 10s on same PDP, nudge Add to Bag with a subtle jiggle */
  const [addToBagDwellJiggle, setAddToBagDwellJiggle] = useState(false);
  const [validationError, setValidationError] = useState('');

  const productPageId = product?.id || product?._id;
  /** PDP view counter — badge on gallery; synced via POST/GET + light polling */
  const [productViewCount, setProductViewCount] = useState(1);

  useEffect(() => {
    setAddToBagDwellJiggle(false);
    if (!productPageId || loading) return undefined;
    const t = window.setTimeout(() => setAddToBagDwellJiggle(true), 10000);
    return () => window.clearTimeout(t);
  }, [productPageId, loading]);
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  const syncProductOpenGraph = useCallback(() => {
    if (!product || loading) return;
    applyProductOpenGraphMeta(product);
  }, [product, loading]);

  useLayoutEffect(() => {
    syncProductOpenGraph();
    return () => {
      applyDocumentBranding(readSiteSettingsFromStorage() || {});
    };
  }, [syncProductOpenGraph]);

  useEffect(() => {
    if (!product) return;
    const fromDoc =
      product.views ??
      product.viewCount ??
      product.viewsCount ??
      product.stats?.views ??
      product.stats?.viewCount;
    const n = fromDoc != null ? Number(fromDoc) : NaN;
    if (Number.isFinite(n) && n >= 1) {
      setProductViewCount(Math.floor(n));
    } else {
      setProductViewCount(1);
    }
  }, [productPageId, product]);

  useEffect(() => {
    if (!productPageId || loading) return;

    let cancelled = false;
    let pollTimer;

    const sync = async () => {
      const fresh = await productService.getProductViewCount(productPageId);
      if (!cancelled && fresh.views != null) {
        setProductViewCount((prev) => Math.max(prev, Math.max(1, fresh.views)));
      }
    };

    const run = async () => {
      if (!shouldSkipDuplicateProductViewPost(productPageId)) {
        const rec = await productService.recordProductView(productPageId);
        if (!cancelled && rec.views != null) {
          setProductViewCount(Math.max(1, rec.views));
          return;
        }
      }
      await sync();
    };

    run();

    pollTimer = window.setInterval(() => {
      sync();
    }, 28000);

    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [productPageId, loading]);
  
  // Related products state
  const [relatedProducts, setRelatedProducts] = useState([]);
  /** Desktop YMAL: which page of 8 (0-based) — advances on an interval when pool > 8 */
  const [ymalDesktopPage, setYmalDesktopPage] = useState(0);
  const relatedProductsYmalRef = useRef([]);

  // Mobile specific states
  const [currentMobileImage, setCurrentMobileImage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDescriptionExpanded, setMobileDescriptionExpanded] = useState(false);
  const [mobileReviewsExpanded, setMobileReviewsExpanded] = useState(false);
  const [mobileVariantSheetOpen, setMobileVariantSheetOpen] = useState(false);
  const [productShareMenuOpen, setProductShareMenuOpen] = useState(false);
  const [shareMenuPos, setShareMenuPos] = useState({ top: 0, left: 0 });
  /** Last clicked share trigger (desktop price row) for menu positioning. */
  const productShareTriggerRef = useRef(null);
  const productShareMenuPortalRef = useRef(null);
  
  // Touch/Swipe states for mobile
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const [isSwipeable, setIsSwipeable] = useState(false);
  
  // Review states
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const [reviewsPagination, setReviewsPagination] = useState(null);
  const [ratingDistribution, setRatingDistribution] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  // Get creator display: { text, isTeamMember } - Team member name or "Super Wear" for admin
  // Profile name takes precedence when creator is current admin (so name updates after profile change)
  const getCreatorDisplay = (createdBy) => {
    if (!createdBy) return null;
    const cb = typeof createdBy === 'object' ? createdBy : null;
    // Admin → "Super Wear" (no quotes)
    if (cb && cb.role === 'admin') return { text: 'Super Wear', isTeamMember: false };
    // Creator is current admin user → use profile name from localStorage (updates when profile changes)
    const profileName = getCreatorDisplayName(createdBy);
    if (profileName) return { text: profileName, isTeamMember: true };
    // Team member with populated name/email from backend
    if (cb && (cb.name || cb.email)) return { text: cb.name || cb.email, isTeamMember: true };
    return { text: 'Super Wear', isTeamMember: false };
  };

  // ✅ Check if user can write review (must have purchased and delivered)
  const canWriteReview = () => {
    if (!user || !product) return false;
    
    const currentProductId = product.id || product._id;
    if (!currentProductId) return false;
    
    // Get user's orders
    const userOrders = getOrdersByUserId(user.id);
    
    // Check if user has any delivered order containing this product
    const hasDeliveredOrder = userOrders.some(order => {
      // Check if order is delivered
      if (order.status !== 'Delivered') return false;
      
      // Check if order contains this product
      return order.items && order.items.some(item => {
        const itemId = item.id || item.productId || item._id;
        return itemId && itemId.toString() === currentProductId.toString();
      });
    });
    
    return hasDeliveredOrder;
  };
  
  // Image Modal/Lightbox states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  
  // Size Chart state
  const [showSizeChart, setShowSizeChart] = useState(false);
  
  // Zoom cursor position
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  
  // Stock management states
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [currentStock, setCurrentStock] = useState(0);
  const [sizeStocks, setSizeStocks] = useState({}); // Store stock for each size
  const [isLoadingSizes, setIsLoadingSizes] = useState(false); // Track if sizes are being loaded

  const pdpVariantSeedAppliedRef = useRef(false);
  useLayoutEffect(() => {
    const seed = pdpMountSeedRef.current;
    if (!seed?.variantUi || pdpVariantSeedAppliedRef.current) return;
    pdpVariantSeedAppliedRef.current = true;
    applyPdpVariantUi(seed.variantUi, {
      setSelectedColor,
      setSelectedSize,
      setCurrentStock,
      setAvailableColors,
      setAvailableSizes,
      setValidationError,
      setSizeStocks,
    });
    const pid = seed.enrichPid;
    if (pid != null && String(pdpEnrichStartedRef.current) !== String(pid)) {
      pdpEnrichStartedRef.current = pid;
      runPdpBackgroundEnrich(pid, seed.enrichSource, seed.enrichColors, setProduct);
    }
  }, [prefetchedProduct]);
  
  // Use ref to track sizeStocks to avoid closure issues
  const sizeStocksRef = useRef({});
  /** Mobile: gallery column ref (layout / scroll-margin); color pick no longer scrolls page to here */
  const mobileGalleryScrollRef = useRef(null);
  const mobileThumbStripRef = useRef(null);
  const mobileCarouselTrackRef = useRef(null);
  const mobileCarouselScrollSkipRef = useRef(false);
  const currentMobileImageRef = useRef(0);
  /** Mobile full-screen image: we pushHistory so device Back closes lightbox instead of leaving PDP */
  const imageModalHistoryPushedRef = useRef(false);

  // Update ref whenever sizeStocks changes
  useEffect(() => {
    sizeStocksRef.current = sizeStocks;
    console.log('🔄 sizeStocksRef updated:', sizeStocks);
  }, [sizeStocks]);

  useEffect(() => {
    const maxQ = Math.min(Math.max(currentStock, 1), 10);
    setQuantity((q) => {
      if (q > maxQ) return maxQ;
      if (q < 1) return 1;
      return q;
    });
  }, [currentStock]);

  useLayoutEffect(() => {
    if (!productShareMenuOpen || !productShareTriggerRef.current) return;
    const anchor = productShareTriggerRef.current;
    const place = () => {
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = 224;
      const margin = 8;
      let left = rect.right - menuWidth;
      left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
      const top = rect.bottom + margin;
      setShareMenuPos({ top, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [productShareMenuOpen]);

  useEffect(() => {
    if (!productShareMenuOpen) return;
    const onPointerDown = (e) => {
      const t = e.target;
      const trig = productShareTriggerRef.current;
      if (trig && (trig === t || trig.contains(t))) return;
      if (productShareMenuPortalRef.current?.contains(t)) return;
      setProductShareMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [productShareMenuOpen]);

  useEffect(() => {
    if (!productShareMenuOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setProductShareMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [productShareMenuOpen]);

  useEffect(() => {
    const loadProduct = async () => {
    setRelatedProducts([]);
    const slugKey = productUrl || id;
    let productData =
      prefetchedProduct ||
      takePrefetchedPdpProduct(slugKey) ||
      findCatalogProductInList(allProducts, { id, productUrl });

    const needsNetworkFetch = !productData && Boolean(id || productUrl);
    if (needsNetworkFetch) {
      setLoading(true);
    }

      try {
    // Determine if we're using old ID-based URL or new SEO URL
    if (!productData && id) {
          // Legacy route: /product/:id — same unwrapping as productService.getProduct (nested data/product)
          try {
            const res = await productService.getProduct(id);
            if (res.success && res.product) {
              productData = res.product;
            }
          } catch (error) {
            // Handle error silently
          }
    } else if (!productData && productUrl) {
          // New SEO route: /:productUrl - Use backend only
          try {
      // First validate the URL format
      const urlInfo = parseProductUrl(productUrl);
      if (urlInfo) {
              // Try to get product by URL directly from backend
              try {
                const response = await productService.getProductByUrl(productUrl);
                if (response.success && response.product) {
                  productData = response.product;
                }
              } catch (urlError) {
                // Fallback to search method
                const searchResponse = await productService.searchProducts(productUrl);
                if (searchResponse.success && searchResponse.products && searchResponse.products.length > 0) {
                  // Find exact match by URL
                  const exactMatch = searchResponse.products.find(p => p.url === productUrl);
                  if (exactMatch) {
                    productData = exactMatch;
                  }
                }
              }
            }
          } catch (error) {
            // Handle error silently
      }
    }
    
    if (productData && isDemoProduct(productData)) {
      productData = null;
    }

    if (productData) {
          const normalizedApi = {
            ...productData,
            images: normalizeProductImagesForStorefront(productData),
          };
          const mapped = mapProductApiToPdpPage(normalizedApi, allProducts);
          if (mapped) {
            const existingId = product?.id ?? product?._id;
            const incomingId = mapped.safeProductData?.id ?? mapped.safeProductData?._id;
            const alreadyShowing =
              existingId &&
              incomingId &&
              String(existingId) === String(incomingId) &&
              !loading;
            if (!alreadyShowing) {
              setProduct(mapped.safeProductData);
              applyPdpVariantUi(mapped.variantUi, {
                setSelectedColor,
                setSelectedSize,
                setCurrentStock,
                setAvailableColors,
                setAvailableSizes,
                setValidationError,
                setSizeStocks,
              });
            }
            const enrichPid = mapped.enrichPid;
            if (
              enrichPid != null &&
              String(pdpEnrichStartedRef.current) !== String(enrichPid)
            ) {
              pdpEnrichStartedRef.current = enrichPid;
              runPdpBackgroundEnrich(
                enrichPid,
                mapped.enrichSource,
                mapped.enrichColors,
                setProduct
              );
            }
          } else {
            setProduct(null);
            setRelatedProducts([]);
          }
    } else {
      setProduct(null);
      setRelatedProducts([]);
    }
      } catch (error) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, productUrl, allProducts, prefetchedProduct]);

  useEffect(() => {
    if (loading || !product) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const related = await loadYmalForProduct(product);
        if (!cancelled) setRelatedProducts(related);
      } catch {
        if (!cancelled) setRelatedProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, product?.id, product?._id, product?.childrenCategory, product?.subcategory, product?.category]);

  useEffect(() => {
    if (!product) return undefined;
    const cb = product.createdBy;
    const creatorId = typeof cb === 'string' ? cb : cb?._id || cb?.id;
    if (!creatorId) return undefined;

    let cancelled = false;

    const applyCreatorPatch = (patch) => {
      if (cancelled || !patch) return;
      setProduct((prev) => {
        if (!prev) return prev;
        const prevCb = prev.createdBy;
        const prevId = typeof prevCb === 'string' ? prevCb : prevCb?._id || prevCb?.id;
        if (String(prevId) !== String(creatorId)) return prev;
        return {
          ...prev,
          createdBy: mergePopulatedCreatedBy(
            patch,
            typeof prevCb === 'object' ? prevCb : { _id: creatorId }
          ),
        };
      });
    };

    (async () => {
      const hasName =
        cb && typeof cb === 'object' && (cb.name || cb.email) && cb.name !== 'Sheen Wear';
      if (!hasName && localStorage.getItem('adminToken')) {
        try {
          const productId = product.id || product._id;
          const creatorRes = await productService.getProductCreatorFromAdmin(productId);
          if (creatorRes.success && creatorRes.name) {
            applyCreatorPatch({
              _id: creatorId,
              name: creatorRes.name,
              role: creatorRes.role || 'team',
            });
          }
        } catch {
          /* optional */
        }
      }

      if (cb && typeof cb === 'object' && normalizeCreatorSlugInput(cb.creatorSlug)) return;
      try {
        const prof = await creatorStorefrontService.getCreatorProfile(creatorId);
        const slug = normalizeCreatorSlugInput(prof?.creator?.creatorSlug);
        if (slug) {
          rememberCreatorSlug(creatorId, slug);
          applyCreatorPatch({ creatorSlug: slug });
        }
      } catch {
        /* optional */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [product?.id, product?._id, product?.createdBy]);

  // Products with no color/size: if nested stock totals to 0, try stock/check for default keys
  /* eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed by id and variant arrays, not full product */
  useEffect(() => {
    if (!product) return;
    const noVariants =
      (!product.colors || product.colors.length === 0) &&
      (!product.sizes || product.sizes.length === 0);
    const singlePlaceholder =
      product.colors?.length === 1 &&
      product.sizes?.length === 1 &&
      isSingleSkuNoPicker(product.colors, product.sizes);
    if (!noVariants && !singlePlaceholder) return;
    const pid = product.id || product._id;
    if (!pid) return;
    if (resolveTotalStock(product, {}) > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const c = noVariants ? 'default' : String(product.colors[0]);
        const s = noVariants ? 'default' : String(product.sizes[0]);
        const r = await productService.checkStockAvailability(pid, c, s);
        if (cancelled) return;
        const n = r?.data?.stock ?? r?.data?.quantity;
        if (r?.success && typeof n === 'number') {
          setCurrentStock(n);
        }
      } catch (e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product?.id, product?._id, product?.colors?.length, product?.sizes?.length]);

  /** Stable signal when per-image linkedColor appears/changes (same images[] ref would skip updates otherwise). */
  const imageLinkedSignature = useMemo(() => {
    if (!product?.images?.length) return '';
    return product.images
      .map((img) => getLinkedColorFromImage(img) || '')
      .join('|');
  }, [product]);

  const productForWishlist = useMemo(() => {
    if (!product) return null;
    const id = product._id || product.id;
    if (id == null || String(id).trim() === '') return null;
    return { ...product, id: String(id) };
  }, [product]);

  /** Logged-in: POST recent-visits (throttled); always update local recent list. */
  useEffect(() => {
    const p = productRef.current;
    if (!p || !productPageId) return;
    trackVisit(p);
  }, [productPageId, trackVisit]);

  /** Desktop: scroll the thumbnail grid so the active tile is visible (spec: auto-focus linked image). */
  const scrollDesktopGalleryToIndex = (idx) => {
    requestAnimationFrame(() => {
      if (typeof window === 'undefined' || window.innerWidth <= 768) return;
      const tiles = document.querySelectorAll(
        '.product-images-section .all-images-grid .image-item'
      );
      if (tiles && tiles[idx]) {
        tiles[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });
  };

  /** When shopper picks a variant color, show the image linked to that color (mobile carousel + desktop selection). */
  useEffect(() => {
    if (!product) return;
    if (!selectedColor) return;
    const primaryIdx = getPrimaryProductImageIndex(product);
    if (isDefaultColorLabel(selectedColor)) {
      setCurrentMobileImage(primaryIdx);
      setSelectedImage(primaryIdx);
      setModalImageIndex(primaryIdx);
      scrollDesktopGalleryToIndex(primaryIdx);
      return;
    }
    let idx = getProductImageIndexForColor(product, selectedColor);
    if (idx < 0) idx = primaryIdx;
    setCurrentMobileImage(idx);
    setSelectedImage(idx);
    setModalImageIndex(idx);
    scrollDesktopGalleryToIndex(idx);
  }, [selectedColor, product?.id, product?.colors, imageLinkedSignature]);

  /** Apply immediately on color tap (spec: first linked image index; fallback = primary). */
  const applyGalleryIndexForSelectedColor = (colorKey, prod) => {
    const p = prod ?? productRef.current ?? product;
    if (!p || colorKey == null || String(colorKey).trim() === '') return;
    const primaryIdx = getPrimaryProductImageIndex(p);
    let idx;
    if (isDefaultColorLabel(colorKey)) {
      idx = primaryIdx;
    } else {
      idx = getProductImageIndexForColor(p, colorKey);
      if (idx < 0) idx = primaryIdx;
    }
    setCurrentMobileImage(idx);
    setSelectedImage(idx);
    setModalImageIndex(idx);
    scrollDesktopGalleryToIndex(idx);
  };

  // DISABLED: Auto-update currentStock via useEffect
  // This was causing conflicts - both handleSizeSelection and useEffect were setting currentStock
  // handleSizeSelection now handles all stock updates directly to avoid double updates and disappearing stock
  // useEffect(() => {
  //   if (selectedSize && selectedColor && sizeStocks && Object.keys(sizeStocks).length > 0 && currentStock === 0) {
  //     const stock = sizeStocks[selectedSize];
  //     if (stock !== undefined) {
  //       setCurrentStock(stock);
  //       setQuantity(prevQuantity => {
  //         if (prevQuantity > stock) {
  //           return Math.min(Math.max(stock, 1), 10);
  //         } else if (stock > 0 && prevQuantity === 0) {
  //           return 1;
  //         }
  //         return prevQuantity;
  //       });
  //     }
  //   }
  // }, [sizeStocks]);

  const handleAddToBag = async (directCheckout = false) => {
    setValidationError('');

    const selectionHint = getAddToBagSelectionHint();
    if (selectionHint) {
      setValidationError(selectionHint);
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(selectionHint, 'error', 3500);
      }
      return;
    }
    if (!isProductAvailable()) {
      setValidationError('Selected item is out of stock');
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast('Selected item is out of stock', 'error', 3500);
      }
      return;
    }
    const qtyToAdd = isMobile ? 1 : quantity;
    if (qtyToAdd > currentStock) {
      setValidationError(`Only ${currentStock} items available in stock`);
      return;
    }

    setAddingToBag(true);
    
    try {
      if (isAuthenticated) {
        const productId = product._id || product.id;
        const response = await cartService.addToCart(
          productId,
          qtyToAdd,
          selectedColor || 'default',
          selectedSize || 'default'
        );
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to add item to cart');
        }
      } else {
        addToGuestBag(product, selectedSize, selectedColor, qtyToAdd);
      }
      
      setValidationError('');
      
      window.dispatchEvent(new CustomEvent('itemAddedToCart'));
      if (isAuthenticated) {
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      }
      
      const sizePart = selectedSize && selectedSize !== DEFAULT_SIZE ? ` (Size: ${selectedSize})` : '';
      const colorPart =
        selectedColor && !isDefaultColorLabel(selectedColor) ? ` (Color: ${selectedColor})` : '';
      const successMsg = directCheckout 
        ? `Added! Redirecting to checkout...`
        : `🛍️ Added ${qtyToAdd} ${product.name}${sizePart}${colorPart} to your bag!`;
      
      if (window.showToast) {
        window.showToast(successMsg, 'bag-success', directCheckout ? 2000 : 5000);
      } else {
        alert(successMsg);
      }

      setMobileVariantSheetOpen(false);
      
      if (directCheckout) {
        navigate('/checkout');
      }
    } catch (error) {
      let userMsg = 'Failed to add item to bag. Please try again.';
      const raw = error?.message || '';
      try {
        if (typeof raw === 'string' && raw.startsWith('{')) {
          const p = JSON.parse(raw);
          if (p.error) userMsg = String(p.error);
        }
      } catch {
        /* ignore */
      }
      console.warn('Add to bag:', userMsg);
      setValidationError(userMsg);
      if (window.showToast) {
        window.showToast(userMsg, 'error', 4500);
      }
    }
    
    setTimeout(() => {
      setAddingToBag(false);
    }, directCheckout ? 500 : 1000);
  };

  const onSizeSelectChange = (e) => {
    const v = e.target.value;
    if (v === '') {
      setSelectedSize('');
      setCurrentStock(0);
      setValidationError('');
      return;
    }
    handleSizeSelection(v, selectedColor);
  };

  const handleColorSelection = async (color) => {
    console.log('🎨 handleColorSelection called:', { color, currentSelectedColor: selectedColor, selectedSize, currentStock });
    
    // Only reset if color is actually changing
    if (selectedColor !== color) {
      setSelectedColor(color);
      applyGalleryIndexForSelectedColor(color, productRef.current);
      // ✅ DON'T reset selectedSize immediately - preserve it
      // setSelectedSize(''); // ❌ REMOVED - This was causing quantity to disappear
      setValidationError('');
      // ✅ DON'T reset currentStock immediately - will be set after API call if size is available
      // setCurrentStock(0); // ❌ REMOVED - This was causing quantity to disappear
      setQuantity(1); // Reset quantity when color changes
      setIsLoadingSizes(true); // Mark that sizes are being loaded
    } else {
      // If same color selected, don't reset anything
      console.log('⚠️ Same color selected, skipping reset');
      return;
    }
    
    try {
      const productId = product.id || product._id || product.productId;
      
      // For products with sizes, fetch sizes from API: GET /api/products/:id/sizes?color=Black
      if (product.sizes && product.sizes.length > 0) {
        console.log('📥 Fetching sizes for color:', color);
        try {
          const sizesResponse = await productService.getProductSizes(productId, color);
          if (sizesResponse.success && sizesResponse.data) {
            const normalized = normalizeSizesApiPayload(sizesResponse.data || {});
            const availableSizesList = normalized.labels;
            const stockMap = normalized.stockMap;
            
            console.log('✅ Sizes loaded:', availableSizesList);
            console.log('✅ Stock data:', stockMap);
            
            // Build sizes with stock information - include ALL sizes (even with 0 stock) for display
            // But filter for availableSizes to show only sizes with stock > 0
            const sizesWithStock = availableSizesList.filter(size => {
              const stock = readSizeStockFromMap(stockMap, size) || 0;
              return stock > 0;
            });
            
            console.log('📦 Available Sizes (with stock > 0):', sizesWithStock);
            console.log('📦 Stock Map (ALL sizes):', stockMap);
            
            // Set states together to avoid race conditions
            // IMPORTANT: Update ref FIRST before state to ensure handleSizeSelection gets latest value
            sizeStocksRef.current = stockMap;
            setSizeStocks(stockMap); // Set sizeStocks FIRST
            setAvailableSizes(sizesWithStock); // Then set availableSizes
            setIsLoadingSizes(false); // Mark that sizes loading is complete
            
            // Log to verify state is set
            console.log('✅ sizeStocks state set:', stockMap);
            console.log('✅ sizeStocksRef updated:', sizeStocksRef.current);
            
            // ✅ If selectedSize exists, check if it's still available in new color
            const availableSizesNames = availableSizesList;
            
            // ✅ Check if selectedSize is still available in new color
            // Use stockMap to check availability (includes all sizes, even with 0 stock)
            const selectedSizeStock = selectedSize ? readSizeStockFromMap(stockMap, selectedSize) : null;
            if (selectedSize && selectedSizeStock !== null) {
              const stock = selectedSizeStock || 0;
              
              if (stock > 0) {
                // ✅ Size is available with stock > 0, update currentStock immediately and PERSIST it
                console.log('🔄 Auto-setting currentStock for pre-selected size:', { selectedSize, stock, currentStock });
                
                // Use functional update to ensure stock is set and persists
                setCurrentStock(prevStock => {
                  if (prevStock !== stock) {
                    console.log('📦 Setting currentStock from', prevStock, 'to', stock);
                    return stock;
                  }
                  console.log('📦 Stock already set correctly:', stock);
                  return prevStock;
                });
                
                setQuantity(prevQuantity => {
                  if (prevQuantity > stock) {
                    return Math.min(Math.max(stock, 1), 10);
                  } else if (stock > 0 && prevQuantity === 0) {
                    return 1;
                  }
                  return prevQuantity;
                });
                
                // ✅ Multiple checks to ensure stock persists (prevent disappearing)
                setTimeout(() => {
                  setCurrentStock(prevStock => {
                    if (prevStock !== stock && stock > 0) {
                      console.log('🔄 Re-setting currentStock after 300ms (was reset):', { prevStock, stock, selectedSize });
                      return stock;
                    }
                    return prevStock;
                  });
                }, 300);
                
                setTimeout(() => {
                  setCurrentStock(prevStock => {
                    if (prevStock !== stock && stock > 0) {
                      console.log('🔄 Re-setting currentStock after 600ms (was reset):', { prevStock, stock, selectedSize });
                      return stock;
                    }
                    return prevStock;
                  });
                }, 600);
                
                setTimeout(() => {
                  setCurrentStock(prevStock => {
                    if (prevStock !== stock && stock > 0) {
                      console.log('🔄 Re-setting currentStock after 1000ms (was reset):', { prevStock, stock, selectedSize });
                      return stock;
                    }
                    return prevStock;
                  });
                }, 1000);
              } else {
                // Size exists but has 0 stock
                console.log('⚠️ Selected size has 0 stock in new color, resetting:', selectedSize);
                setSelectedSize('');
                setCurrentStock(0);
              }
            } else if (selectedSize && !availableSizesNames.includes(selectedSize)) {
              // ✅ Size not available in new color at all
              console.log('⚠️ Selected size not available in new color, resetting:', selectedSize);
              setSelectedSize('');
              setCurrentStock(0);
            }

            if (!productHasSelectableSizes(availableSizesList)) {
              const defaultSz = resolveDefaultSizeFromList(availableSizesList);
              if (defaultSz) {
                let stock = readSizeStockFromMap(stockMap, defaultSz);
                if (stock === null) stock = readSizeStockFromMap(stockMap, 'default');
                if (stock === null && product.stock?.[color] && typeof product.stock[color] === 'object') {
                  const cell =
                    product.stock[color][defaultSz] ?? product.stock[color].default ?? product.stock[color].Default;
                  if (cell !== undefined) stock = parseInt(cell, 10) || 0;
                }
                setSelectedSize(defaultSz);
                setCurrentStock(stock ?? 0);
              }
            }
            // ✅ REMOVED: Don't reset currentStock if no size is selected - let user select size first
            // The issue was that this was resetting currentStock even when size was about to be selected
          } else {
            // Fallback: Use product sizes
            setAvailableSizes(product.sizes);
            setSizeStocks({});
            sizeStocksRef.current = {}; // Update ref
            setIsLoadingSizes(false);
            if (!productHasSelectableSizes(product.sizes || [])) {
              const defaultSz = resolveDefaultSizeFromList(product.sizes || []);
              if (defaultSz) {
                setSelectedSize(defaultSz);
                const cell = product.stock?.[color]?.[defaultSz] ?? product.stock?.[color]?.default;
                if (cell !== undefined) setCurrentStock(parseInt(cell, 10) || 0);
              }
            }
          }
        } catch (error) {
          console.error('❌ Error fetching sizes:', error);
          // Fallback: Use product sizes
          setAvailableSizes(product.sizes);
          setSizeStocks({});
          sizeStocksRef.current = {}; // Update ref
          setIsLoadingSizes(false);
          if (!productHasSelectableSizes(product.sizes || [])) {
            const defaultSz = resolveDefaultSizeFromList(product.sizes || []);
            if (defaultSz) {
              setSelectedSize(defaultSz);
              const cell = product.stock?.[color]?.[defaultSz] ?? product.stock?.[color]?.default;
              if (cell !== undefined) setCurrentStock(parseInt(cell, 10) || 0);
            }
          }
        }
        
        // For unstitched products, get stock from API
      } else {
        // For unstitched products (no sizes), check stock via API
        try {
          const stockResponse = await productService.checkStockAvailability(productId, color, '');
          if (stockResponse.success && stockResponse.data) {
            setCurrentStock(stockResponse.data.stock || 0);
          } else if (product.stock && product.stock[color]) {
            // Fallback to product data
            const stock = typeof product.stock[color] === 'number' ? product.stock[color] : 0;
            setCurrentStock(stock);
          }
        } catch (error) {
          console.error('Error fetching color stock:', error);
          // Fallback to product data
          if (product.stock && product.stock[color]) {
            const stock = typeof product.stock[color] === 'number' ? product.stock[color] : 0;
            setCurrentStock(stock);
          }
        }
      }
    } catch (error) {
      console.error('Error handling color selection:', error);
      // Fallback to product data
      if (product.stock && product.stock[color]) {
        const colorStock = product.stock[color];
        if (typeof colorStock === 'object') {
          // Only include sizes with stock > 0
          const sizesFromStock = Object.keys(colorStock).filter(size => {
            const stock = parseInt(colorStock[size]) || 0;
            return stock > 0;
          });
          setAvailableSizes(sizesFromStock);
          
          // Build stock map for fallback
          const stockMap = {};
          Object.keys(colorStock).forEach(size => {
            stockMap[size] = parseInt(colorStock[size]) || 0;
          });
          setSizeStocks(stockMap);
          // Update ref immediately to avoid closure issues
          sizeStocksRef.current = stockMap;
        } else if (product.sizes && product.sizes.length > 0) {
          // If no stock data, show all sizes but mark as unavailable
          setAvailableSizes([]);
        }
        if (typeof colorStock === 'number') {
          setCurrentStock(colorStock);
        }
      } else if (product.sizes && product.sizes.length > 0) {
        // If no stock data, show all sizes but mark as unavailable
        setAvailableSizes([]);
      }
    }
  };

  const handleSizeSelection = async (size, color = null) => {
    console.log('🎯 handleSizeSelection called:', { size, color, selectedColor, sizeStocks: sizeStocksRef.current, isLoadingSizes, currentStock });
    
    // Use provided color or fallback to selectedColor
    const colorToUse = color || selectedColor;
    
    if (!colorToUse || !product || !product.id) {
      console.log('❌ Missing color or product:', { colorToUse, product: !!product });
      setCurrentStock(0);
      return;
    }
    
    // Only update selectedSize if it's different
    if (selectedSize !== size) {
      setSelectedSize(size);
      setValidationError('');
    } else if (selectedSize === size && currentStock > 0) {
      // If same size selected and stock already set, verify it's still correct
      const currentStockFromState = sizeStocksRef.current && sizeStocksRef.current[size] !== undefined 
        ? sizeStocksRef.current[size] 
        : null;
      
      if (currentStockFromState !== null && currentStockFromState === currentStock) {
        console.log('✅ Same size selected and stock already set correctly, keeping:', { size, currentStock });
        return;
      } else if (currentStockFromState !== null && currentStockFromState !== currentStock) {
        // Stock changed, update it
        console.log('🔄 Stock changed, updating:', { size, oldStock: currentStock, newStock: currentStockFromState });
        setCurrentStock(currentStockFromState);
        return;
      }
    }

    // Wait for sizes to finish loading if they're still loading
    // Retry mechanism to wait for sizeStocks to be populated
    let retries = 0;
    const maxRetries = 20; // Wait up to 2 seconds (20 * 100ms)
    
    while (isLoadingSizes && retries < maxRetries) {
      console.log('⏳ Waiting for sizes to load...', { retries, isLoadingSizes, sizeStocks: sizeStocksRef.current });
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
      
      // Re-check sizeStocks from ref after each wait (ref always has latest value)
      if (sizeStocksRef.current && sizeStocksRef.current[size] !== undefined) {
        console.log('✅ Found stock during wait:', { size, stock: sizeStocksRef.current[size] });
        break;
      }
    }

    // First check if we have stock data from sizeStocks (set during color selection)
    // Use ref to get latest value (avoids closure issues)
    let stockFromState = readSizeStockFromMap(sizeStocksRef.current, size);
    
    // If still not found and we were loading, wait a bit more and check again
    if (stockFromState === null && (retries > 0 || isLoadingSizes)) {
      console.log('⏳ Retrying to get stock from sizeStocks...', { size, sizeStocks: sizeStocksRef.current, retries });
      // Wait a bit more for state to update
      await new Promise(resolve => setTimeout(resolve, 300));
      // Re-check sizeStocks from ref (always has latest value)
      stockFromState = readSizeStockFromMap(sizeStocksRef.current, size);
    }
    
    if (stockFromState !== null) {
      const stock = stockFromState || 0;
      console.log('📦 Using stock from sizeStocks:', { size, stock, sizeStocks: sizeStocksRef.current, currentStock });
      
      // Set stock immediately using functional update to ensure it's set
      setCurrentStock(prevStock => {
        if (prevStock !== stock) {
          console.log('📦 Setting currentStock:', { prevStock, stock });
          return stock;
        }
        console.log('📦 Stock already set, keeping:', prevStock);
        return prevStock;
      });
      
      // Reset quantity if it exceeds available stock
      setQuantity(prevQuantity => {
        if (prevQuantity > stock) {
          return Math.min(Math.max(stock, 1), 10);
        } else if (stock > 0 && prevQuantity === 0) {
          return 1;
        }
        return prevQuantity;
      });
      
      console.log('✅ Stock set from sizeStocks:', stock);
      
      // Double-check after a brief delay to ensure stock persists (prevent disappearing)
      setTimeout(() => {
        setCurrentStock(prevStock => {
          if (prevStock !== stock && stock > 0) {
            console.log('🔄 Re-setting currentStock after delay (was reset):', { prevStock, stock });
            return stock;
          }
          return prevStock;
        });
      }, 200);
      
      return; // Don't make API call if we already have the data
    }
    
    console.log('⚠️ Stock not found in sizeStocks, fetching from API:', { size, sizeStocks: sizeStocksRef.current, isLoadingSizes, retries });

    // If sizeStocks doesn't have the data, make API call
    try {
      console.log('📥 Fetching stock via API for:', { color: colorToUse, size });
      // Use stock API to check availability for real-time stock
      const productId = product.id || product._id || product.productId;
      const stockResponse = await productService.checkStockAvailability(productId, colorToUse, size);
      
      if (stockResponse.success && stockResponse.data) {
        const stock = stockResponse.data.stock || 0;
        console.log('✅ Stock from API:', stock);
        setCurrentStock(stock);
        
        // Update sizeStocks state with the fetched stock
        setSizeStocks(prev => ({
          ...prev,
          [size]: stock
        }));
        
        // Reset quantity if it exceeds available stock
        if (quantity > stock) {
          setQuantity(Math.min(Math.max(stock, 1), 10));
        }
      } else {
        // Fallback to product data if API fails
        if (product.stock && product.stock[colorToUse]) {
          const colorStock = product.stock[colorToUse];
          let stock = 0;
          
          if (typeof colorStock === 'number') {
            stock = colorStock;
          } else if (colorStock[size]) {
            stock = parseInt(colorStock[size]) || 0;
          }
          
          console.log('📦 Stock from product data:', stock);
          setCurrentStock(stock);
          
          // Update sizeStocks state
          setSizeStocks(prev => ({
            ...prev,
            [size]: stock
          }));
          
          if (quantity > stock) {
            setQuantity(Math.min(Math.max(stock, 1), 10));
          }
        } else {
          setCurrentStock(0);
        }
      }
    } catch (error) {
      console.error('❌ Error checking stock via API:', error);
      
      // Fallback to product data
      if (product.stock && product.stock[colorToUse]) {
        const colorStock = product.stock[colorToUse];
        let stock = 0;
        
        if (typeof colorStock === 'number') {
          stock = colorStock;
        } else if (colorStock[size]) {
          stock = parseInt(colorStock[size]) || 0;
        }
        
        console.log('📦 Stock from fallback:', stock);
        setCurrentStock(stock);
        
        // Update sizeStocks state
        setSizeStocks(prev => ({
          ...prev,
          [size]: stock
        }));
      } else {
        setCurrentStock(0);
      }
    }
  };

  const isProductAvailable = () => {
    if (!product) return false;
    const needsColorPick = productHasSelectableColors(
      getColorListForProductPicker(product, availableColors)
    );
    const needsSizePick = productHasSelectableSizes(getSizeListForProductPicker(product, availableSizes));

    if (needsSizePick && !selectedSize) return false;
    if (needsColorPick && !selectedColor) return false;
    return currentStock > 0;
  };

  const isProductCompletelyOutOfStock = () => {
    if (!product) return true;
    const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
    const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
    if (!hasSizes && !hasColors) {
      return resolveTotalStock(product, {}) <= 0 && currentStock <= 0;
    }
    if (!product.stock || typeof product.stock !== 'object') return true;
    for (const color of Object.keys(product.stock)) {
      const colorStock = product.stock[color];
      if (typeof colorStock === 'number' && colorStock > 0) return false;
      if (colorStock && typeof colorStock === 'object') {
        for (const size of Object.keys(colorStock)) {
          if (parseInt(colorStock[size], 10) > 0) return false;
        }
      }
    }
    return true;
  };

  /** When Add to Bag is blocked only by missing colour/size — used for click feedback + styling */
  const getAddToBagSelectionHint = () => {
    if (!product) return null;
    const needColor =
      productHasSelectableColors(getColorListForProductPicker(product, availableColors)) &&
      !selectedColor;
    const needSize =
      productHasSelectableSizes(getSizeListForProductPicker(product, availableSizes)) && !selectedSize;
    if (needColor && needSize) return 'Please select colour, size';
    if (needColor) return 'Please select colour';
    if (needSize) return 'Please select size';
    return null;
  };

  const addToBagNeedsSelectionOnly =
    !!product &&
    !addingToBag &&
    !isProductCompletelyOutOfStock() &&
    !isProductAvailable() &&
    getAddToBagSelectionHint() !== null;

  const sizeChoices = filterSelectableSizes(
    availableSizes.length > 0 ? availableSizes : (product?.sizes || [])
  );

  const handleWhatsAppOrder = () => {
    const phoneDigits = getWhatsAppOrderPhoneDigits();
    let message = `🛍️ *Order via WhatsApp* 🛍️\n\n`;
    message += `*Product:* ${product.name}\n`;
    message += `*Price:* PKR ${product.price?.toLocaleString()}\n`;
    message += `*Quantity:* ${quantity}\n`;

    if (selectedSize && selectedSize !== DEFAULT_SIZE) {
      message += `*Size:* ${selectedSize}\n`;
    }

    if (selectedColor && !isDefaultColorLabel(selectedColor)) {
      message += `*Color:* ${selectedColor}\n`;
    }

    if (product.category) {
      message += `*Category:* ${safeRender(product.category, 'General')}\n`;
    }

    message += `\n*Total Amount:* PKR ${(product.price * quantity)?.toLocaleString()}\n\n`;
    message += `Please confirm this order and provide delivery details.\n\n`;
    message += `Product Link: ${window.location.href}`;

    const whatsappUrl = buildWhatsAppUrl(phoneDigits, message);
    if (!whatsappUrl) {
      setValidationError(
        'WhatsApp order number is not set. Add it in Admin → My Account (WhatsApp).'
      );
      return;
    }

    window.open(whatsappUrl, '_blank');
  };

  const handleProductShareMenuToggle = (e) => {
    e.stopPropagation();
    productShareTriggerRef.current = e.currentTarget;
    setProductShareMenuOpen((o) => !o);
  };

  const handleProductNativeShare = async () => {
    if (!product) return;
    const { url, title, text } = buildProductShareMeta(product);
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setProductShareMenuOpen(false);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    setProductShareMenuOpen(true);
  };

  const handleProductShareCopyLink = async () => {
    if (!product) return;
    const copied = await copyProductShareToClipboard(product);
    setProductShareMenuOpen(false);
    if (copied && typeof window.showToast === 'function') {
      window.showToast('Product details and link copied', 'success', 2500);
    }
  };

  const handleProductFacebookShare = async () => {
    if (!product) return;
    const { opened, copied } = await openFacebookProductShare(product);
    setProductShareMenuOpen(false);
    if (typeof window.showToast === 'function') {
      if (opened && copied) {
        window.showToast(
          'Product details copied — paste in your Facebook post (Ctrl+V)',
          'success',
          4500
        );
      } else if (opened) {
        window.showToast('Facebook share opened', 'info', 2500);
      }
    }
  };

  const displayRelatedProducts = useMemo(() => {
    const pool = filterCatalogProducts(relatedProducts);
    if (!pool.length) return [];
    if (isMobile) {
      return pool.slice(0, YMAL_PAGE_SIZE);
    }
    const total = pool.length;
    const pages = Math.max(1, Math.ceil(total / YMAL_PAGE_SIZE));
    const page = ((ymalDesktopPage % pages) + pages) % pages;
    return sliceYmalDesktopWindow(pool, page, YMAL_PAGE_SIZE);
  }, [relatedProducts, isMobile, ymalDesktopPage]);

  const productShareMeta = useMemo(() => {
    if (!product) return null;
    return buildProductShareMeta(product);
  }, [product]);

  // Mirror public WhatsApp settings into localStorage (same source as Admin → My Account)
  useEffect(() => {
    let cancelled = false;
    settingsService
      .getContactChannels()
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || res;
        if (d && typeof d === 'object') {
          persistContactChannelsLocal(d);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Mobile detection and handlers
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!mobileVariantSheetOpen || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileVariantSheetOpen]);

  useEffect(() => {
    if (isMobile) return;
    setMobileVariantSheetOpen(false);
  }, [isMobile]);

  /** When only default/As image colors exist, auto-select and load sizes/stock once per product. */
  const hiddenDefaultColorInitKeyRef = useRef('');
  useEffect(() => {
    if (!product) return;
    const productKey = String(product.id || product._id || '');
    const colorList = product.colors || [];
    if (productHasSelectableColors(colorList) || colorList.length === 0) return;

    const defaultColor = resolveDefaultColorFromList(colorList);
    if (!defaultColor) return;
    if (selectedColor !== defaultColor) return;

    const initKey = `${productKey}:${defaultColor}`;
    if (hiddenDefaultColorInitKeyRef.current === initKey) return;
    hiddenDefaultColorInitKeyRef.current = initKey;

    handleColorSelection(defaultColor);
  }, [product, selectedColor]);

  useEffect(() => {
    relatedProductsYmalRef.current = relatedProducts;
  }, [relatedProducts]);

  useEffect(() => {
    setYmalDesktopPage(0);
  }, [id, productUrl, product?.id, product?._id]);

  /** Desktop: every 10s show the next 8 products when pool has more than 8 */
  useEffect(() => {
    if (isMobile) return;
    if (relatedProducts.length <= YMAL_PAGE_SIZE) return undefined;
    const idTimer = window.setInterval(() => {
      const len = relatedProductsYmalRef.current.length;
      if (len <= YMAL_PAGE_SIZE) return;
      const pages = Math.ceil(len / YMAL_PAGE_SIZE);
      setYmalDesktopPage((p) => (p + 1) % pages);
    }, YMAL_DESKTOP_ROTATE_MS);
    return () => window.clearInterval(idTimer);
  }, [isMobile, relatedProducts.length, id, productUrl]);

  // Touch/Swipe handlers for mobile images
  const handleTouchStart = (e) => {
    setTouchEnd({ x: 0, y: 0 }); // Reset touch end
    const touch = e.targetTouches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY
    });
    setIsSwipeable(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwipeable) return;
    const touch = e.targetTouches[0];
    setTouchEnd({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.x || !touchEnd.x || !isSwipeable) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const minSwipeDistance = 50;
    
    // Check if it's more horizontal than vertical swipe
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe - change image
      if (Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          // Swipe left - next image
          nextMobileImage();
        } else {
          // Swipe right - previous image
          prevMobileImage();
        }
        if (e && e.preventDefault) {
          e.preventDefault(); // Prevent page scroll for horizontal swipes
        }
      }
    }
    // Vertical swipes are allowed to scroll the page normally
    
    setIsSwipeable(false);
  };

  const nextMobileImage = () => {
    const n = getGallerySlideCount();
    if (n <= 1) return;
    setCurrentMobileImage((prev) => {
      const next = (prev + 1) % n;
      setSelectedImage(next);
      setModalImageIndex(next);
      return next;
    });
  };

  const prevMobileImage = () => {
    const n = getGallerySlideCount();
    if (n <= 1) return;
    setCurrentMobileImage((prev) => {
      const next = (prev - 1 + n) % n;
      setSelectedImage(next);
      setModalImageIndex(next);
      return next;
    });
  };


  const handleGalleryWishlistClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!productForWishlist) return;
    const wid = productForWishlist.id;
    if (isLiked(wid)) {
      removeFromLikedItems(wid);
    } else {
      addToLikedItems(productForWishlist);
    }
  };

  // Image Modal/Lightbox handlers
  const openImageModal = (index) => {
    setModalImageIndex(index);
    setIsZoomed(false);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    if (isMobile && typeof window !== 'undefined' && window.history?.pushState) {
      try {
        window.history.pushState({ pdpImageLightbox: 1 }, '', window.location.href);
        imageModalHistoryPushedRef.current = true;
      } catch {
        imageModalHistoryPushedRef.current = false;
      }
    }
  };

  const closeImageModal = () => {
    const hadPushed = imageModalHistoryPushedRef.current;
    imageModalHistoryPushedRef.current = false;
    setIsModalOpen(false);
    setIsZoomed(false);
    document.body.style.overflow = 'unset'; // Restore scroll
    if (hadPushed && isMobile && typeof window !== 'undefined' && window.history?.back) {
      window.history.back();
    }
  };

  const nextModalImage = () => {
    const n = getGallerySlideCount();
    if (n <= 1) return;
    setModalImageIndex((prev) => (prev + 1) % n);
  };

  const prevModalImage = () => {
    const n = getGallerySlideCount();
    if (n <= 1) return;
    setModalImageIndex((prev) => (prev - 1 + n) % n);
  };

  const goToModalImage = (index) => {
    setModalImageIndex(index);
  };

  const toggleZoom = () => {
    setIsZoomed((z) => !z);
  };

  useEffect(() => {
    setIsZoomed(false);
  }, [modalImageIndex]);

  // Handle mouse movement for zoom effect
  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomPosition({ x, y });
  };

  const handleMouseLeave = () => {
    if (isZoomed) {
      setZoomPosition({ x: 50, y: 50 }); // Center position
    }
  };

  // Mobile: OS / browser Back pops our lightbox history entry — close modal, stay on PDP
  useEffect(() => {
    if (!isMobile) return;
    const onPopState = () => {
      setIsModalOpen((open) => {
        if (!open) return open;
        imageModalHistoryPushedRef.current = false;
        document.body.style.overflow = 'unset';
        return false;
      });
      setIsZoomed(false);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isMobile]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeImageModal();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevModalImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextModalImage();
          break;
        case ' ':
          e.preventDefault();
          toggleZoom();
          break;
        default:
          break;
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, modalImageIndex]);

  // Fetch reviews for the product
  const fetchReviews = async (page = 1) => {
    const productId = product?._id || product?.id || product?.productId;
    if (!productId) return;
    
    setReviewsLoading(true);
    try {
      const response = await reviewService.getProductReviews(String(productId), {
        page,
        limit: 10,
        sort: 'newest'
      });
      
      // Handle multiple backend response formats
      let reviewsList = response.data?.reviews ?? response.reviews ?? (Array.isArray(response.data) ? response.data : []);
      const pagination = response.data?.pagination ?? response.pagination ?? null;
      const total = response.data?.total ?? response.total ?? reviewsList.length;
      const ratingDistribution = response.data?.ratingDistribution ?? response.ratingDistribution ?? [];
      
      // Sort newest first (recent reviews at top)
      if (Array.isArray(reviewsList) && reviewsList.length > 0) {
        reviewsList = [...reviewsList].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0).getTime();
          const dateB = new Date(b.createdAt || b.created_at || 0).getTime();
          return dateB - dateA;
        });
      }
      
      setReviews(Array.isArray(reviewsList) ? reviewsList : []);
      setReviewsPagination(pagination);
      setRatingDistribution(Array.isArray(ratingDistribution) ? ratingDistribution : []);
      setTotalReviews(typeof total === 'number' ? total : reviewsList.length);
      
      // Calculate average rating
      const dist = Array.isArray(ratingDistribution) ? ratingDistribution : [];
      if (dist.length > 0) {
        const totalRating = dist.reduce((sum, item) => sum + ((item._id ?? item.rating ?? 0) * (item.count ?? 0)), 0);
        const totalCount = dist.reduce((sum, item) => sum + (item.count ?? 0), 0);
        setAverageRating(totalCount > 0 ? (totalRating / totalCount).toFixed(1) : 0);
      } else if (reviewsList.length > 0) {
        const avg = reviewsList.reduce((s, r) => s + (r.rating || 0), 0) / reviewsList.length;
        setAverageRating(avg.toFixed(1));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Load reviews when product is loaded
  useEffect(() => {
    const productId = product?._id || product?.id || product?.productId;
    if (productId) {
      fetchReviews(1);
    }
  }, [product?._id, product?.id, product?.productId]);

  // Handle review form submission
  const handleReviewSubmitted = (newReview) => {
    setShowReviewForm(false);
    // Refresh reviews to show the new one
    fetchReviews(currentReviewPage);
  };

  // Handle review update
  const handleReviewUpdate = (updatedReview) => {
    // Refresh reviews
    fetchReviews(currentReviewPage);
  };

  // Handle review deletion
  const handleReviewDelete = (deletedReview) => {
    // Refresh reviews
    fetchReviews(currentReviewPage);
  };

  // Load more reviews
  const loadMoreReviews = () => {
    if (reviewsPagination?.hasNextPage) {
      fetchReviews(currentReviewPage + 1);
      setCurrentReviewPage(prev => prev + 1);
    }
  };

  const getProductImages = () => {
    if (!product) {
      return [];
    }

    // Enhanced Cloudinary image processing
    const rawImages = Array.isArray(product?.images) ? product.images : [];
    
    // Enhanced Cloudinary image URL processing
    const urls = rawImages
      .map((img, index) => {
        if (typeof img === 'string') {
          const url = img.trim() !== '' ? img.replace('http://', 'https://') : null;
          return url;
        } else if (img && typeof img === 'object') {
          // Handle Cloudinary object with metadata
          const url = img.url || img.fullPath || img.src || img.path;
          const processedUrl = url && url.trim() !== '' ? url.replace('http://', 'https://') : null;
          return processedUrl;
        }
        return null;
      })
      .filter(Boolean);
    
    if (urls.length > 0) {
      return urls;
    }
    
    // Fallback to single image - handle Cloudinary objects
    if (typeof product?.image === 'string' && product.image.trim() !== '') {
      const url = product.image.replace('http://', 'https://');
      return [url];
    } else if (product?.image && typeof product.image === 'object') {
      // Handle Cloudinary object with metadata
      const url = product.image.url || product.image.fullPath || product.image.src || product.image.path;
      if (url && url.trim() !== '') {
        const processedUrl = url.replace('http://', 'https://');
        return [processedUrl];
      }
    }
    
    // Final fallback
    return [];
  };

  /**
   * Gallery slide count aligned with `product.images` indices (same as getProductImageIndexForColor).
   * `getProductImages()` filters out bad URLs, so its length can differ — do not use it for color→index mapping.
   */
  const getGallerySlideCount = () => {
    if (!product) return 0;
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images.length;
    }
    return getProductImages().length;
  };

  /** URL at slide index (aligned with getProductImageIndexForColor / primary index). */
  const getGalleryUrlAt = (index) => {
    if (!product) return null;
    if (Array.isArray(product.images) && product.images.length > 0) {
      if (index < 0 || index >= product.images.length) return null;
      const img = product.images[index];
      if (typeof img === 'string') {
        const u = img.trim();
        return u ? u.replace('http://', 'https://') : null;
      }
      if (img && typeof img === 'object') {
        const u = img.url || img.fullPath || img.src || img.path || img.cloudinaryUrl;
        return u && u.trim() ? u.replace('http://', 'https://') : null;
      }
      return null;
    }
    const urls = getProductImages();
    if (!urls.length) return null;
    const i = Math.min(Math.max(0, index), urls.length - 1);
    return urls[i] ?? urls[0];
  };

  /** Mobile: switch carousel to index (sync modal index; used by thumbnails + arrows/swipe). */
  const goToMobileGalleryIndex = (index) => {
    const n = getGallerySlideCount();
    if (n <= 0) return;
    const i = Math.max(0, Math.min(index, n - 1));
    setCurrentMobileImage(i);
    setSelectedImage(i);
    setModalImageIndex(i);
  };

  useEffect(() => {
    if (!isMobile) return;
    const root = mobileThumbStripRef.current;
    if (!root) return;
    const active = root.querySelector('.mobile-gallery-thumb.active');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentMobileImage, isMobile, product?.id, product?.images?.length]);

  useEffect(() => {
    currentMobileImageRef.current = currentMobileImage;
  }, [currentMobileImage]);

  /** Mobile multi-image: sync horizontal scroll position when index changes (arrows, thumbs, color). */
  useLayoutEffect(() => {
    if (!isMobile) return;
    const n = getGallerySlideCount();
    if (n <= 1) return;
    const track = mobileCarouselTrackRef.current;
    if (!track) return;
    const w = track.clientWidth;
    if (w <= 0) return;
    const target = currentMobileImage * w;
    if (Math.abs(track.scrollLeft - target) < 2) return;
    mobileCarouselScrollSkipRef.current = true;
    track.scrollTo({ left: target, behavior: 'auto' });
    const t = window.setTimeout(() => {
      mobileCarouselScrollSkipRef.current = false;
    }, 80);
    return () => clearTimeout(t);
  }, [currentMobileImage, isMobile, product?.id, product?.images?.length]);

  /** Mobile multi-image: index follows horizontal scroll (swipe / scrollbar). */
  useEffect(() => {
    if (!isMobile) return;
    const track = mobileCarouselTrackRef.current;
    if (!track) return;
    const n = getGallerySlideCount();
    if (n <= 1) return;

    let raf = 0;
    const onScroll = () => {
      if (mobileCarouselScrollSkipRef.current) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = track.clientWidth;
        if (w <= 0) return;
        let i = Math.round(track.scrollLeft / w);
        i = Math.max(0, Math.min(i, n - 1));
        setCurrentMobileImage((prev) => {
          if (prev === i) return prev;
          setSelectedImage(i);
          setModalImageIndex(i);
          return i;
        });
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      track.removeEventListener('scroll', onScroll);
    };
  }, [isMobile, product?.id, product?.images?.length]);

  /** Keep slide aligned to current index when carousel width changes (rotate, font scale). */
  useEffect(() => {
    if (!isMobile) return;
    const track = mobileCarouselTrackRef.current;
    if (!track) return;

    let resizeSkipTimer = 0;
    const sync = () => {
      if (getGallerySlideCount() <= 1) return;
      const w = track.clientWidth;
      if (w <= 0) return;
      mobileCarouselScrollSkipRef.current = true;
      track.scrollTo({ left: currentMobileImageRef.current * w, behavior: 'auto' });
      if (resizeSkipTimer) window.clearTimeout(resizeSkipTimer);
      resizeSkipTimer = window.setTimeout(() => {
        resizeSkipTimer = 0;
        mobileCarouselScrollSkipRef.current = false;
      }, 80);
    };

    window.addEventListener('resize', sync);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    if (ro) ro.observe(track);
    return () => {
      window.removeEventListener('resize', sync);
      if (resizeSkipTimer) window.clearTimeout(resizeSkipTimer);
      if (ro) ro.disconnect();
    };
  }, [isMobile, product?.id, product?.images?.length]);

  /** Product showcase video URL from API (see docs/BACKEND_PROMPT_PRODUCT_VIDEO.md). */
  const getProductVideoUrl = () => {
    if (!product) return null;
    const raw =
      product.videoUrl ||
      (product.video && typeof product.video === 'object'
        ? product.video.url || product.video.secure_url
        : null) ||
      product.promoVideoUrl;
    if (typeof raw !== 'string' || !raw.trim()) return null;
    return raw.replace('http://', 'https://');
  };

  // Color mapping function
  const getColorValue = (colorName) => {
    // Safely handle colorName - ensure it's a string
    if (!colorName) {
      return '#D4AF37'; // Default golden color
    }
    
    // Convert to string if it's not already
    const colorString = typeof colorName === 'string' ? colorName : String(colorName);

    const fromHex = normalizeHexColor(colorString);
    if (fromHex) return fromHex;

    const colorMap = {
      // Golden shades
      'Golden': '#D4AF37',
      'Light Gold': '#F0E68C',
      'Dark Gold': '#B8860B',
      'Cream Gold': '#F5DEB3',
      'Golden Yellow': '#FFD700',
      'Golden Beige': '#E6D2AA',
      'Gold': '#FFD700',
      
      // Basic colors
      'Cream': '#F5F5DC',
      'White': '#FFFFFF',
      'Black': '#000000',
      'Navy': '#000080',
      'Navy Blue': '#000080',
      'Beige': '#F5F5DC',
      'Brown': '#A0522D',
      'Gray': '#808080',
      'Grey': '#808080',
      'Silver': '#C0C0C0',
      
      // Blue shades
      'Blue': '#0000FF',
      'Light Blue': '#ADD8E6',
      'Sky Blue': '#87CEEB',
      'Royal Blue': '#4169E1',
      'Turquoise': '#40E0D0',
      'Teal': '#008080',
      
      // Pink shades
      'Pink': '#FFC0CB',
      'Rose Pink': '#FF69B4',
      'Dusty Pink': '#DCAE96',
      'Peach': '#FFCBA4',
      'Light Pink': '#FFB6C1',
      'Fuchsia': '#FF00FF',
      'Magenta': '#FF00FF',
      
      // Green shades
      'Green': '#008000',
      'Sage Green': '#9CAF88',
      'Emerald': '#50C878',
      'Mint': '#98FB98',
      'Mint Green': '#98FB98',
      'Olive': '#808000',
      'Forest Green': '#228B22',
      
      // Purple shades
      'Purple': '#800080',
      'Lavender': '#E6E6FA',
      'Violet': '#8B00FF',
      
      // Red shades
      'Red': '#FF0000',
      'Ruby': '#E0115F',
      'Maroon': '#800000',
      'Crimson': '#DC143C',
      
      // Orange shades
      'Orange': '#FFA500',
      'Coral': '#FF7F50',
      
      // Yellow shades
      'Yellow': '#FFFF00',
      'Lemon': '#FFFACD',
      
      // “As in photo” / legacy default (neutral swatch only)
      'As image': '#9E9E9E',
      'Default color': '#9E9E9E',

      // Neutral colors
      'Nude': '#E3C4A8',
      'Taupe': '#D2B48C',
      'Khaki': '#F0E68C',
      'Ivory': '#FFFFF0',
      'Off White': '#FAF0E6',
      
      // Print and Pattern Colors (Smart Pattern Detection)
      'Floral Print': '#FFB6C1', // Light pink for floral patterns
      'Blue Print': '#4169E1',   // Royal blue for blue prints
      'Green Print': '#228B22',  // Forest green for green prints
      'Red Print': '#DC143C',    // Crimson for red prints
      'Black Print': '#2F2F2F',  // Dark gray for black prints
      'White Print': '#F8F8FF',  // Ghost white for white prints
      'Paisley Print': '#8B00FF', // Violet for paisley patterns
      'Geometric Print': '#FF7F50', // Coral for geometric patterns
      'Striped': '#4682B4',      // Steel blue for stripes
      'Checkered': '#708090',    // Slate gray for checkered
      'Polka Dot': '#FF69B4',    // Hot pink for polka dots
      'Abstract Print': '#9370DB', // Medium purple for abstract
      'Digital Print': '#00CED1', // Dark turquoise for digital prints
      'Traditional Print': '#B8860B', // Dark golden rod for traditional
      'Modern Print': '#20B2AA',  // Light sea green for modern prints
      'Vintage Print': '#CD853F', // Peru for vintage patterns
      'Ethnic Print': '#DAA520',  // Golden rod for ethnic patterns
      'Animal Print': '#D2691E',  // Chocolate for animal prints
      'Tie Dye': '#FF6347',      // Tomato for tie dye
      'Ombre': '#9932CC'         // Dark orchid for ombre
    };
    
    // Check for exact match first
    if (colorMap[colorString]) {
      return colorMap[colorString];
    }
    
    // Smart color detection for complex names
    const lowerName = colorString.toLowerCase();
    
    // Check if it contains print/pattern keywords
    if (lowerName.includes('print') || lowerName.includes('pattern')) {
      if (lowerName.includes('blue')) return '#4169E1';
      if (lowerName.includes('red')) return '#DC143C';
      if (lowerName.includes('green')) return '#228B22';
      if (lowerName.includes('pink')) return '#FFB6C1';
      if (lowerName.includes('black')) return '#2F2F2F';
      if (lowerName.includes('white')) return '#F8F8FF';
      if (lowerName.includes('floral')) return '#FFB6C1';
      if (lowerName.includes('geometric')) return '#FF7F50';
      return '#9370DB'; // Default print color
    }
    
    // Check for basic color keywords in complex names
    if (lowerName.includes('blue')) return '#0000FF';
    if (lowerName.includes('red')) return '#FF0000';
    if (lowerName.includes('green')) return '#008000';
    if (lowerName.includes('pink')) return '#FFC0CB';
    if (lowerName.includes('yellow')) return '#FFFF00';
    if (lowerName.includes('orange')) return '#FFA500';
    if (lowerName.includes('purple')) return '#800080';
    if (lowerName.includes('brown')) return '#A0522D';
    if (lowerName.includes('black')) return '#000000';
    if (lowerName.includes('white')) return '#FFFFFF';
    if (lowerName.includes('gray') || lowerName.includes('grey')) return '#808080';
    if (lowerName.includes('gold')) return '#FFD700';
    if (lowerName.includes('silver')) return '#C0C0C0';
    if (lowerName.includes('cream')) return '#F5F5DC';
    if (lowerName.includes('beige')) return '#F5F5DC';
    if (lowerName.includes('navy')) return '#000080';
    
    // Default to golden if no match found
    return '#D4AF37';
  };

  // Get contrasting text color
  const getContrastColor = (bgColor) => {
    // Convert hex to RGB
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const renderStockAvailabilityNote = (stockQty) => {
    if (stockQty === 1) {
      return (
        <>
          Available: <span className="low-stock-text">Only 1 left</span>
        </>
      );
    }
    if (stockQty <= 5) {
      return (
        <>
          Available: {stockQty} in stock
          <br />
          <span className="low-stock-text">(Low Stock)</span>
        </>
      );
    }
    return <>Available: {stockQty} in stock</>;
  };

  const bumpQuantity = (delta, stockQty) => {
    const maxQ = Math.min(Math.max(stockQty, 1), 10);
    setQuantity((prev) => {
      const next = Number(prev || 1) + delta;
      return Math.min(maxQ, Math.max(1, next));
    });
  };

  const renderLowStockInlineQty = (stockQty) => {
    if (isMobile) return null;
    if (stockQty <= 1 || stockQty > 5) return null;
    return (
      <span className="stock-inline-qty" aria-label="Low stock quantity selector">
        <span className="stock-inline-qty-label">Qty:</span>
        <button
          type="button"
          className="stock-inline-qty-btn"
          onClick={() => bumpQuantity(-1, stockQty)}
          disabled={quantity <= 1}
        >
          -
        </button>
        <span className="stock-inline-qty-value">{quantity}</span>
        <button
          type="button"
          className="stock-inline-qty-btn"
          onClick={() => bumpQuantity(1, stockQty)}
          disabled={quantity >= Math.min(Math.max(stockQty, 1), 10)}
        >
          +
        </button>
      </span>
    );
  };

  const renderStyledSizeLabel = (sizeDisplay) => {
    const text = String(sizeDisplay || '').trim();
    const parts = text.split('=').map((p) => p.trim()).filter(Boolean);
    if (parts.length !== 2) return text;

    return (
      <span className="size-option-label-parts">
        <span className="size-option-eu">{parts[0]}</span>
        <span className="size-option-separator">=</span>
        <span className="size-option-uk">{parts[1]}</span>
      </span>
    );
  };

  // Helper function to get category slug for navigation (now returns hierarchical path)
  const getCategorySlugLocal = (category) => {
    if (!category) return 'general';
    
    // Try to get hierarchical slug first
    if (typeof category === 'object') {
      // Use hierarchical slug utility if we have category data
      // Note: We don't have allCategories here, so it will fall back to simple slug
      const hierarchicalSlug = getCategorySlug(category, allCategories);
      if (hierarchicalSlug) return hierarchicalSlug;
      
      // Fallback to category slug or name
      return category.slug || category.name || category.fullPath || 'general';
    }
    
    // If category is a string, return as is
    return String(category).toLowerCase();
  };

  const renderProductCategoryLinks = (mobile = false) => {
    if (productCategoryLinks.length === 0) {
      return (
        <button
          type="button"
          className={`category-link-btn${mobile ? ' mobile' : ''}`}
          onClick={() => {
            const categorySlug = getCategorySlugLocal(product?.category);
            navigate(`/category/${categorySlug}`);
          }}
          title={`View all ${safeRender(product?.category, 'General')} products`}
        >
          {safeRender(product?.category, 'General')}
        </button>
      );
    }

    return (
      <span className="product-category-tags">
        {productCategoryLinks.map((link, index) => (
          <button
            key={link.id || link.slug || index}
            type="button"
            className={`category-link-btn${index > 0 ? ' subcategory-link-btn' : ''}${mobile ? ' mobile' : ''}`}
            onClick={() => navigate(`/category/${link.slug}`)}
            title={`View all ${link.label} products`}
          >
            {link.label}
          </button>
        ))}
      </span>
    );
  };

  // Helper function to safely render any value as string
  const safeRender = (value, fallback = '') => {
    if (value === null || value === undefined) {
      return fallback;
    }
    if (typeof value === 'object') {
      // Handle category objects
      if (value.name) return value.name;
      if (value.slug) return value.slug;
      if (value.id) return value.id;
      // For other objects, try to convert to string
      try {
        return JSON.stringify(value);
      } catch {
        return fallback;
      }
    }
    return String(value);
  };

  if (productUrl && !id) {
    const key = String(productUrl).toLowerCase();
    if (Object.prototype.hasOwnProperty.call(RESERVED_STATIC_PATHS, key)) {
      return <Navigate to={RESERVED_STATIC_PATHS[key]} replace />;
    }
  }

  if (loading && !product) {
    return (
      <div className="product-page">
        <SiteLoading
          title="Loading product"
          subtitle="Just a moment…"
          icon="👕"
          variant="full"
        />
      </div>
    );
  }

  if (!product || !product.name) {
    return (
      <div className="product-page">
        <div className="product-not-found-container">
          <div className="not-found-content">
            {/* Animated 404 Icon */}
            <div className="not-found-icon">
              <div className="shopping-cart-404">
                <div className="cart-body-404">
                  <div className="cart-handle-404"></div>
                  <div className="cart-wheels-404">
                    <div className="wheel-404 left"></div>
                    <div className="wheel-404 right"></div>
                  </div>
                </div>
                <div className="question-mark">?</div>
              </div>
            </div>

            {/* Main Content */}
            <div className="not-found-text">
              <h1 className="not-found-title">
                <span className="title-main">Oops!</span>
                <span className="title-sub">Product Not Found</span>
              </h1>
              <p className="not-found-description">
                The product you're looking for seems to have vanished into the digital void! 
                It might be out of stock, discontinued, or just playing hide and seek.
              </p>
            </div>

            {/* Fun Suggestions */}
            <div className="not-found-suggestions">
              <h3>What you can do instead:</h3>
              <div className="suggestion-list">
                <div className="suggestion-item">
                  <span className="suggestion-icon">🔍</span>
                  <span>Search for similar products</span>
                </div>
                <div className="suggestion-item">
                  <span className="suggestion-icon">🛍️</span>
                  <span>Browse our latest collection</span>
                </div>
                <div className="suggestion-item">
                  <span className="suggestion-icon">⭐</span>
                  <span>Check out featured items</span>
                </div>
                <div className="suggestion-item">
                  <span className="suggestion-icon">📱</span>
                  <span>Contact us for help</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="not-found-actions">
              <button 
                onClick={() => navigate('/')} 
                className="btn-home"
              >
                <span className="btn-icon">🏠</span>
                <span>Return to Home</span>
              </button>
              <button 
                onClick={() => navigate('/products')} 
                className="btn-browse"
              >
                <span className="btn-icon">🛍️</span>
                <span>Browse Products</span>
              </button>
              <button 
                onClick={() => window.history.back()} 
                className="btn-back"
              >
                <span className="btn-icon">←</span>
                <span>Go Back</span>
              </button>
            </div>

            {/* Fun Footer */}
            <div className="not-found-footer">
              <p>Don't worry, even the best shoppers get lost sometimes! 😊</p>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="floating-elements">
            <div className="floating-item item-1">👕</div>
            <div className="floating-item item-2">👗</div>
            <div className="floating-item item-3">👔</div>
            <div className="floating-item item-4">👜</div>
            <div className="floating-item item-5">👟</div>
            <div className="floating-item item-6">⌚</div>
          </div>
        </div>
      </div>
    );
  }

  // Debug: Log current product and images before render
  const currentImages = getProductImages();
  const isNoVariantProduct =
    product && (product.colors?.length ?? 0) === 0 && (product.sizes?.length ?? 0) === 0;

  const isSingleSkuQtyOnlyProduct =
    product && isSingleSkuNoPicker(product.colors, product.sizes);

  const showQuantityOnlyMode = isSingleSkuQtyOnlyProduct || isNoVariantProduct;

  const showSizeQtyRow =
    !showQuantityOnlyMode &&
    productHasSelectableSizes(getSizeListForProductPicker(product, availableSizes));

  const cleanedDescription = stripEmbedFromDescriptionForDisplay(product?.description || '');
  const legacyDescriptionBlocks = mergeDescriptionRichSources(
    product?.descriptionRich,
    product?.description || ''
  );
  const hasLegacyRich =
    legacyDescriptionBlocks && hasRenderableRichContent(legacyDescriptionBlocks);
  const hasHtmlBody = isLikelyHtmlDescription(cleanedDescription);
  const visibleTextLen = (cleanedDescription || '').replace(/<[^>]+>/g, '').trim().length;
  const hasPlainDescription =
    visibleTextLen > 0 && cleanedDescription !== 'Product description';
  const showDescriptionSection = hasLegacyRich || hasHtmlBody || hasPlainDescription;

  const handleMobileStickyAddClick = () => {
    setValidationError('');
    if (isProductCompletelyOutOfStock()) return;
    const needsColor = productHasSelectableColors(
      getColorListForProductPicker(product, availableColors)
    );
    const needsSize = productHasSelectableSizes(getSizeListForProductPicker(product, availableSizes));
    if (showQuantityOnlyMode || (!needsColor && !needsSize)) {
      handleAddToBag(false);
      return;
    }
    if (getAddToBagSelectionHint() === null && isProductAvailable()) {
      handleAddToBag(false);
      return;
    }
    setMobileVariantSheetOpen(true);
  };

  const handleMobileSheetConfirmAdd = () => {
    handleAddToBag(false);
  };

  const renderProductColorSwatches = (extraClass = '') => {
    if (showQuantityOnlyMode) return null;
    const colorListRaw = getColorListForProductPicker(product, availableColors);
    if (!productHasSelectableColors(colorListRaw)) return null;

    const colorList = filterSelectableColors(colorListRaw);

    return (
      <div
        className={`product-options product-options--color-swatches product-info-mobile-order--color ${extraClass}`.trim()}
      >
        <span className="product-option-label">
          Please select color <span className="required-indicator">*</span>
        </span>
        <div className="color-options">
          {colorList.map((color) => {
            const colorName =
              typeof color === 'string' ? color : (color?.name || color?.value || color?.color || String(color));
            const colorLabel = getColorDisplayName(colorName, colorList);
            const bgColor = getColorValue(colorName);
            const textColor = getContrastColor(bgColor);

            let colorStock = 0;
            if (product.stock && product.stock[colorName]) {
              if (typeof product.stock[colorName] === 'number') {
                colorStock = product.stock[colorName];
              } else if (product.sizes) {
                colorStock = Object.values(product.stock[colorName] || {}).reduce(
                  (sum, stock) => sum + (parseInt(stock, 10) || 0),
                  0
                );
              }
            }

            const hasStock = colorStock > 0;
            const isSelected =
              selectedColor === colorName ||
              (typeof selectedColor === 'string' && selectedColor === colorName);

            return (
              <button
                key={colorName}
                type="button"
                className={`color-option ${isSelected ? 'selected' : ''} ${!hasStock ? 'out-of-stock' : ''}`}
                style={{
                  backgroundColor: bgColor,
                  color: textColor,
                  border: `2px solid ${textColor === '#FFFFFF' ? '#333333' : '#CCCCCC'}`,
                  textShadow:
                    textColor === '#FFFFFF' ? '1px 1px 2px rgba(0,0,0,0.8)' : '1px 1px 2px rgba(255,255,255,0.8)',
                  opacity: hasStock ? 1 : 0.5,
                  position: 'relative',
                }}
                onClick={() => {
                  if (hasStock) {
                    handleColorSelection(colorName);
                  }
                }}
                disabled={!hasStock}
                title={`${colorLabel}${!hasStock ? ' - Out of Stock' : ` - ${colorStock} available`}`}
              >
                {colorLabel}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderProductSizeSection = (extraClass = '') => {
    if (!showSizeQtyRow) return null;

    return (
      <div
        className={`product-options product-options--size-qty product-info-mobile-order--size-qty ${extraClass}`.trim()}
      >
        <div className="size-qty-heading-row">
          <span className="product-option-label">
            Please select size <span className="required-indicator">*</span>
          </span>
          <button
            type="button"
            className="size-chart-link size-chart-link--heading"
            onClick={() => setShowSizeChart(true)}
          >
            📏 Size Chart
          </button>
        </div>
        <div className="size-qty-row">
          <div className="size-qty-row__size">
            <div className="size-options size-options--compact" role="group" aria-label="Select size">
              {sizeChoices.length > 0
                ? sizeChoices.map((size) => {
                    const sizeName = getSizeLabel(size);
                    let sizeStock = 0;
                    if (selectedColor) {
                      const fromRef = readSizeStockFromMap(sizeStocksRef.current, sizeName);
                      const fromState = readSizeStockFromMap(sizeStocks, sizeName);
                      if (fromRef !== null) {
                        sizeStock = fromRef;
                      } else if (fromState !== null) {
                        sizeStock = fromState;
                      } else if (
                        product.stock &&
                        product.stock[selectedColor] &&
                        product.stock[selectedColor][sizeName]
                      ) {
                        sizeStock = parseInt(product.stock[selectedColor][sizeName], 10) || 0;
                      }
                    }
                    const hasStock = sizeStock > 0;
                    const isSelected = selectedSize === sizeName;
                    const sizeDisplay = getStorefrontSizeDisplay(sizeName, product);
                    return (
                      <button
                        key={sizeName}
                        type="button"
                        className={`size-option ${isSelected ? 'selected' : ''} ${!hasStock ? 'out-of-stock' : ''}`}
                        onClick={() => hasStock && handleSizeSelection(sizeName, selectedColor)}
                        disabled={isLoadingSizes || !selectedColor || !hasStock}
                        aria-label={sizeName}
                        title={`${sizeDisplay}${hasStock ? ` - ${sizeStock} in stock` : ' - Out of stock'}`}
                      >
                        {renderStyledSizeLabel(sizeDisplay)}
                      </button>
                    );
                  })
                : null}
            </div>
            {sizeChoices.length === 0 && <p className="no-sizes-message">No sizes available</p>}
          </div>
        </div>
        {currentStock > 0 && (
          <div className="stock-info stock-info--size-qty">
            <span className="stock-label">
              <i className="fas fa-check-circle"></i> {renderStockAvailabilityNote(currentStock)}
              {renderLowStockInlineQty(currentStock)}
            </span>
          </div>
        )}
        {currentStock === 0 &&
          selectedColor &&
          (selectedSize || !product.sizes || product.sizes.length === 0) && (
            <div className="stock-info stock-info--size-qty">
              <span className="stock-label out-of-stock-label">
                <i className="fas fa-times-circle"></i> Out of Stock
              </span>
            </div>
          )}
      </div>
    );
  };

  const renderMobileProductMetaDetails = () => {
    const hasCategory = productCategoryLinks.length > 0;
    const hasBrand = Boolean(product.brand);
    const hasCreator = Boolean(product.createdBy && getCreatorDisplay(product.createdBy));
    const hasFabric = Boolean(product.fabric);
    if (!hasCategory && !hasBrand && !hasCreator && !hasFabric) return null;

    return (
      <div className="product-details-info product-details-info--mobile product-info-mobile-order--meta">
        {hasCategory && (
          <div className="detail-item detail-item--categories">
            <strong>Category:</strong>
            {renderProductCategoryLinks(true)}
          </div>
        )}
        {hasBrand && (
          <div className="detail-item">
            <strong>Brand:</strong>
            <button
              type="button"
              className="brand-link-btn mobile"
              onClick={() => {
                const brandSlug = String(product.brand).toLowerCase().replace(/\s+/g, '-');
                navigate(`/brand/${brandSlug}`);
              }}
              title={`View all ${product.brand} products`}
            >
              <i className="fas fa-copyright" style={{ marginRight: '4px', fontSize: '10px' }} />
              {product.brand}
            </button>
          </div>
        )}
        {hasCreator && (
          <div className="detail-item detail-item--creator">
            {renderCreatorBlock('product-creator--mobile-inline')}
          </div>
        )}
        {hasFabric && (
          <div className="detail-item">
            <strong>Fabric:</strong> {product.fabric}
          </div>
        )}
      </div>
    );
  };

  const renderCreatorBlock = (extraClassName = '') => {
    if (!product.createdBy) return null;
    const creator = getCreatorDisplay(product.createdBy);
    if (!creator) return null;
    const shopPath = getCreatorShopLinkPath(product.createdBy);
    if (!shopPath) return null;
    return (
      <div className={`product-creator-inline ${extraClassName}`.trim()}>
        <div className="product-creator-above-reviews-inner">
          <i className="fas fa-user-edit" aria-hidden="true" />
          <span>
            Created by{' '}
            <Link
              to={shopPath}
              className={`creator-name creator-name--link ${creator.isTeamMember ? 'team-member' : ''}`}
              title={`View all products by ${creator.text}`}
            >
              {creator.text}
            </Link>
          </span>
        </div>
      </div>
    );
  };

  const renderReviewsSection = (extraClassName = '', options = {}) => {
    const { mobileAccordion = false, expanded = false, onToggle } = options;

    const reviewsInner = (
      <>
      <div className={`reviews-header${mobileAccordion ? ' reviews-header--accordion' : ''}`}>
        {!mobileAccordion && <h2 className="reviews-title">Customer Reviews</h2>}
        <div className="reviews-summary">
          {totalReviews > 0 ? (
            <>
              <div className="average-rating">
                <span className="rating-number">{averageRating}</span>
                <div className="rating-stars">
                  {Array.from({ length: 5 }, (_, index) => (
                    <i
                      key={index}
                      className={`fas fa-star ${index < Math.floor(averageRating) ? 'filled' : 'empty'}`}
                    />
                  ))}
                </div>
                <span className="rating-text">({totalReviews} reviews)</span>
              </div>
              {canWriteReview() && (
                <button type="button" className="write-review-btn" onClick={() => setShowReviewForm(true)}>
                  <i className="fas fa-pen" />
                  Write a Review
                </button>
              )}
            </>
          ) : (
            <div className="no-reviews">
              {canWriteReview() && (
                <button type="button" className="write-review-btn" onClick={() => setShowReviewForm(true)}>
                  <i className="fas fa-pen" />
                  Write a Review
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showReviewForm && (
        <ReviewForm
          product={product}
          onReviewSubmitted={handleReviewSubmitted}
          onCancel={() => setShowReviewForm(false)}
        />
      )}

      {reviewsLoading ? (
        <div className="reviews-loading">
          <i className="fas fa-spinner fa-spin" />
          <span>Loading reviews...</span>
        </div>
      ) : reviews.length > 0 ? (
        <>
          <div className="reviews-list">
            {reviews.map((review) => (
              <ReviewCard
                key={review._id}
                review={review}
                onUpdate={handleReviewUpdate}
                onDelete={handleReviewDelete}
              />
            ))}
          </div>

          {reviewsPagination?.hasNextPage && (
            <div className="load-more-reviews">
              <button type="button" className="load-more-btn" onClick={loadMoreReviews}>
                Load More Reviews
              </button>
            </div>
          )}
        </>
      ) : (
        !showReviewForm && (
          <div className="no-reviews-message">
            <i className="fas fa-comment-slash" />
            <p>No reviews available for this product yet.</p>
          </div>
        )
      )}
      </>
    );

    if (mobileAccordion) {
      const reviewLabel =
        totalReviews > 0 ? `Customer Reviews (${totalReviews})` : 'Customer Reviews';
      return (
        <div
          className={`product-details-expandable product-reviews-expandable product-info-mobile-order--reviews ${extraClassName}`.trim()}
          id="product-reviews"
        >
          <button type="button" className="details-header" onClick={onToggle}>
            <h3>{reviewLabel}</h3>
            <span className={`details-toggle ${expanded ? 'expanded' : ''}`}>▼</span>
          </button>
          <div className={`details-content ${expanded ? 'expanded' : ''}`}>
            <div className="reviews-section reviews-section--mobile-accordion-inner">{reviewsInner}</div>
          </div>
        </div>
      );
    }

    return (
      <div className={`reviews-section ${extraClassName}`.trim()} id="product-reviews">
        {reviewsInner}
      </div>
    );
  };

  const showcaseVideoUrl = getProductVideoUrl();

  return (
    <div className="product-page">
      
      {/* Container 1: Product Layout Grid */}
      <div className="product-layout">
          {/* Container 3: Product Images Section */}
          <div ref={mobileGalleryScrollRef} className="product-images-section">
            {isMobile ? (
              // Mobile: images first, then video (if attached), then color/title in info column
              <>
                <div className="mobile-image-carousel">
                  {getGallerySlideCount() > 1 ? (
                    <div
                      ref={mobileCarouselTrackRef}
                      className="mobile-image-carousel-track"
                      role="region"
                      aria-label="Product images — swipe or scroll sideways"
                    >
                      {Array.from({ length: getGallerySlideCount() }, (_, index) => (
                        <div key={`slide-${index}`} className="mobile-carousel-slide">
                          <img
                            src={
                              getGalleryUrlAt(index) ||
                              getGalleryUrlAt(0) ||
                              undefined
                            }
                            alt={`${product.name} - View ${index + 1}`}
                            className="mobile-main-image mobile-main-image--carousel"
                            onClick={() => openImageModal(index)}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="mobile-image-carousel-track mobile-image-carousel-track--single"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      <div className="mobile-carousel-slide">
                        <img
                          key="mobile-0"
                          src={
                            getGalleryUrlAt(0) ||
                            undefined
                          }
                          alt={`${product.name} - View 1`}
                          className="mobile-main-image"
                          onClick={() => openImageModal(0)}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Mobile Navigation Arrows */}
                  {getGallerySlideCount() > 1 && (
                    <>
                      <button 
                        className="mobile-nav-arrow mobile-prev-arrow"
                        onClick={prevMobileImage}
                        title="Previous Image"
                      >
                        ←
                      </button>
                      <button 
                        className="mobile-nav-arrow mobile-next-arrow"
                        onClick={nextMobileImage}
                        title="Next Image"
                      >
                        →
                      </button>
                    </>
                  )}
                  
                  {/* Tags only on the default (first) image — match desktop behavior */}
                  {currentMobileImage === 0 && (
                    <>
                      <div
                        className="mobile-image-view-badge product-views-eye"
                        role="status"
                        aria-live="polite"
                        aria-label={`${productViewCount} views`}
                      >
                        <span className="product-views-eye__count">
                          {productViewCount.toLocaleString()} Views
                        </span>
                      </div>

                      {product.isNew && (
                        <span className={`product-badge new ${product.isOnSale ? 'with-sale' : ''}`}>
                          New In
                        </span>
                      )}
                      {product.isOnSale && (
                        <span className={`product-badge sale ${product.isNew ? 'with-new' : ''}`}>
                          Sale
                        </span>
                      )}
                    </>
                  )}
                </div>
                
                {/* Mobile: small thumbnails — tap switches main image; tap main = full screen */}
                {getGallerySlideCount() > 1 && (
                  <div
                    ref={mobileThumbStripRef}
                    className="mobile-gallery-thumbs"
                    role="tablist"
                    aria-label="Product images"
                  >
                    {Array.from({ length: getGallerySlideCount() }, (_, index) => {
                      const thumbSrc = getGalleryUrlAt(index);
                      return (
                        <button
                          type="button"
                          key={index}
                          role="tab"
                          aria-selected={index === currentMobileImage}
                          className={`mobile-gallery-thumb ${index === currentMobileImage ? 'active' : ''}`}
                          onClick={() => goToMobileGalleryIndex(index)}
                        >
                          {thumbSrc ? (
                            <img src={thumbSrc} alt="" draggable={false} />
                          ) : (
                            <span className="mobile-gallery-thumb-placeholder" aria-hidden />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {showcaseVideoUrl && (
                  <div className="product-video-wrap product-video-wrap--mobile product-video-wrap--after-gallery">
                    <video
                      src={showcaseVideoUrl}
                      controls
                      playsInline
                      preload="metadata"
                      className="product-showcase-video"
                    />
                    <span className="product-video-label product-video-label--mobile">Video</span>
                  </div>
                )}
              </>
            ) : (
              // Desktop: same method as mobile (one main image + thumbnail strip)
              (() => {
                let imagesToShow = [];
                if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                  imagesToShow = product.images
                    .map((img) => {
                      if (typeof img === 'string' && img.trim() !== '') return img;
                      if (img && typeof img === 'object') return img.url || img.fullPath || img.src || img.path;
                      return null;
                    })
                    .filter(Boolean);
                } else if (product.image) {
                  if (typeof product.image === 'string' && product.image.trim() !== '') {
                    imagesToShow = [product.image];
                  } else if (product.image && typeof product.image === 'object') {
                    const url = product.image.url || product.image.fullPath || product.image.src || product.image.path;
                    if (url && url.trim() !== '') imagesToShow = [url];
                  }
                }

                const desktopActiveIndex = Math.min(
                  Math.max(selectedImage, 0),
                  Math.max(imagesToShow.length - 1, 0)
                );
                const desktopMainImage = imagesToShow[desktopActiveIndex];

                return (
                  <div className="desktop-gallery-layout">
                    {desktopMainImage ? (
                      <div className="desktop-main-image-wrap">
                        <div
                          className="desktop-main-image-view-badge product-views-eye"
                          role="status"
                          aria-live="polite"
                          aria-label={`${productViewCount} views`}
                        >
                          <span className="product-views-eye__count">
                            {productViewCount.toLocaleString()} Views
                          </span>
                        </div>
                        <button
                          type="button"
                          className="desktop-main-image-zoom-hit"
                          onClick={() => openImageModal(desktopActiveIndex)}
                          title="Click to zoom image"
                        >
                          <img
                            src={desktopMainImage}
                            alt={`${product.name} ${desktopActiveIndex + 1}`}
                            className="desktop-main-image"
                            onLoad={(e) => {
                              e.target.style.opacity = '1';
                              setImageLoadingStates((prev) => ({
                                ...prev,
                                [desktopActiveIndex]: 'loaded',
                              }));
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              setImageLoadingStates((prev) => ({
                                ...prev,
                                [desktopActiveIndex]: 'error',
                              }));
                            }}
                            style={{
                              opacity: imageLoadingStates[desktopActiveIndex] === 'loaded' ? 1 : 0.7,
                              transition: 'opacity 0.3s ease',
                            }}
                          />
                          {product.isNew && desktopActiveIndex === 0 && (
                            <span className={`product-badge new ${product.isOnSale ? 'with-sale' : ''}`}>New In</span>
                          )}
                          {product.isOnSale && desktopActiveIndex === 0 && (
                            <span className={`product-badge sale ${product.isNew ? 'with-new' : ''}`}>Sale</span>
                          )}
                        </button>
                      </div>
                    ) : null}

                    {imagesToShow.length > 1 ? (
                      <div className="desktop-gallery-thumbs" role="tablist" aria-label="Product images">
                        {imagesToShow.map((image, index) => (
                          <button
                            key={`desktop-thumb-${index}`}
                            type="button"
                            role="tab"
                            aria-selected={index === desktopActiveIndex}
                            className={`desktop-gallery-thumb ${index === desktopActiveIndex ? 'active' : ''}`}
                            onClick={() => setSelectedImage(index)}
                            title={`Show image ${index + 1}`}
                          >
                            <img src={image} alt="" draggable={false} />
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {showcaseVideoUrl ? (
                      <div className="product-video-wrap product-video-wrap--after-gallery">
                        <video
                          src={showcaseVideoUrl}
                          controls
                          playsInline
                          preload="metadata"
                          className="product-showcase-video"
                        />
                        <span className="product-video-label">Video</span>
                      </div>
                    ) : null}
                  </div>
                );
              })()
            )}
            {!isMobile && (() => {
              const tags = product.tags && Array.isArray(product.tags) ? product.tags : [];
              const matStr = product.material && String(product.material).trim();
              if (!tags.length && !matStr) return null;
              const tagItems = buildMaterialTagNavItems(tags);
              return (
                <div className="product-material-section product-material-section--below-gallery">
                  <h3 className="product-material-heading">Material</h3>
                  <div className="product-material-body">
                    <div className="product-material-ribbon" role="group" aria-label="Material and tags">
                      {matStr ? (
                        <span className="product-material-ribbon__text">{matStr}</span>
                      ) : null}
                      {tagItems.map(({ tagValue, tagSlug, index }, i) => (
                        <React.Fragment key={`material-tag-${tagSlug}-${index}`}>
                          {(matStr || i > 0) && (
                            <span className="product-material-ribbon__sep" aria-hidden="true">
                              /
                            </span>
                          )}
                          <button
                            type="button"
                            className="product-material-ribbon__link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToProductTagListing(navigate, tagValue);
                            }}
                            title={`View all ${tagValue} products`}
                          >
                            {tagValue}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
            {!isMobile && showDescriptionSection && (
              <div className="product-description product-description--below-gallery">
                <h3 className="product-description-heading">Description</h3>
                {hasLegacyRich ? (
                  <ProductDescriptionRich blocks={legacyDescriptionBlocks} />
                ) : hasHtmlBody ? (
                  <ProductDescriptionHtml html={cleanedDescription} />
                ) : (
                  <p>{cleanedDescription}</p>
                )}
              </div>
            )}
          </div>

          {/* Right Side - Product Information */}
          <div className="product-info-section">
            <div className="product-title-row product-info-mobile-order--title">
              <h1 className="product-title">{product.name}</h1>
            </div>

            {/* Material — slash-separated ribbon; tags stay clickable → /tag/:slug */}
            {isMobile && (() => {
              const tags = product.tags && Array.isArray(product.tags) ? product.tags : [];
              const matStr = product.material && String(product.material).trim();
              if (!tags.length && !matStr) return null;
              const tagItems = buildMaterialTagNavItems(tags);
              return (
                <div className="product-material-section product-info-mobile-order--material">
                  <h3 className="product-material-heading">Material</h3>
                  <div className="product-material-body">
                    <div className="product-material-ribbon" role="group" aria-label="Material and tags">
                      {matStr ? (
                        <span className="product-material-ribbon__text">{matStr}</span>
                      ) : null}
                      {tagItems.map(({ tagValue, tagSlug, index }, i) => (
                        <React.Fragment key={`material-tag-${tagSlug}-${index}`}>
                          {(matStr || i > 0) && (
                            <span className="product-material-ribbon__sep" aria-hidden="true">
                              /
                            </span>
                          )}
                          <button
                            type="button"
                            className="product-material-ribbon__link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToProductTagListing(navigate, tagValue);
                            }}
                            title={`View all ${tagValue} products`}
                          >
                            {tagValue}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <div className="product-price product-info-mobile-order--price">
              <div className="price-container">
                <div className="price-container__prices">
                  {product.originalPrice && product.originalPrice > product.price ? (
                    <>
                      <span className="current-price">Rs.{product.price.toLocaleString()}.00</span>
                      <span className="original-price">Rs.{product.originalPrice.toLocaleString()}.00</span>
                      <span className="discount">
                        {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="current-price">Rs.{product.price.toLocaleString()}.00</span>
                  )}
                </div>
                <div className="product-share-wrap">
                  {/* Wishlist button — always visible (guest wishlist supported by context). */}
                  <button
                    type="button"
                    className={`product-price-wishlist-btn ${productForWishlist && isLiked(productForWishlist.id) ? 'is-liked' : ''}`}
                    onClick={handleGalleryWishlistClick}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label={
                      productForWishlist && isLiked(productForWishlist.id)
                        ? 'Remove from wishlist'
                        : 'Add to wishlist'
                    }
                    title="Wishlist"
                  >
                    <ProductWishlistHeartIcon
                      filled={!!(productForWishlist && isLiked(productForWishlist.id))}
                    />
                  </button>
                  <button
                    type="button"
                    className="product-share-trigger"
                    onClick={handleProductShareMenuToggle}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-expanded={productShareMenuOpen}
                    aria-haspopup="true"
                    aria-label="Share product on social media"
                    title="Share"
                  >
                    <i className="fas fa-share-alt" aria-hidden />
                  </button>
                </div>
                {productShareMenuOpen &&
                  productShareMeta &&
                  typeof document !== 'undefined' &&
                  createPortal(
                    <div
                      ref={productShareMenuPortalRef}
                      className="product-share-menu product-share-menu--portal"
                      role="menu"
                      style={{
                        position: 'fixed',
                        top: shareMenuPos.top,
                        left: shareMenuPos.left,
                        zIndex: 10050,
                      }}
                    >
                      {typeof navigator !== 'undefined' && typeof navigator.share === 'function' ? (
                        <button
                          type="button"
                          role="menuitem"
                          className="product-share-menu__item"
                          onClick={() => handleProductNativeShare()}
                        >
                          <i className="fas fa-mobile-alt" aria-hidden />
                          Share via device
                        </button>
                      ) : null}
                      <a
                        role="menuitem"
                        className="product-share-menu__item product-share-menu__link"
                        href={`https://wa.me/?text=${encodeURIComponent(productShareMeta.message)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setProductShareMenuOpen(false)}
                      >
                        <i className="fab fa-whatsapp" aria-hidden />
                        WhatsApp
                      </a>
                      <button
                        type="button"
                        role="menuitem"
                        className="product-share-menu__item"
                        onClick={handleProductFacebookShare}
                      >
                        <i className="fab fa-facebook-f" aria-hidden />
                        Facebook
                      </button>
                      <a
                        role="menuitem"
                        className="product-share-menu__item product-share-menu__link"
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                          productShareMeta.message
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setProductShareMenuOpen(false)}
                      >
                        <i className="fab fa-x-twitter" aria-hidden />
                        X (Twitter)
                      </a>
                      <button
                        type="button"
                        role="menuitem"
                        className="product-share-menu__item"
                        onClick={handleProductShareCopyLink}
                      >
                        <i className="fas fa-copy" aria-hidden />
                        Copy text & link
                      </button>
                    </div>,
                    document.body
                  )}
              </div>
            </div>

            {isMobile && renderMobileProductMetaDetails()}

            {isMobile && showDescriptionSection && (
              <div className="product-details-expandable product-description-expandable product-info-mobile-order--desc">
                <button
                  type="button"
                  className="details-header"
                  onClick={() => setMobileDescriptionExpanded((v) => !v)}
                >
                  <h3>Description</h3>
                  <span className={`details-toggle ${mobileDescriptionExpanded ? 'expanded' : ''}`}>▼</span>
                </button>
                <div className={`details-content ${mobileDescriptionExpanded ? 'expanded' : ''}`}>
                  <div className="mobile-description-text">
                    {hasLegacyRich ? (
                      <ProductDescriptionRich blocks={legacyDescriptionBlocks} />
                    ) : hasHtmlBody ? (
                      <ProductDescriptionHtml html={cleanedDescription} />
                    ) : (
                      <p>{cleanedDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isMobile &&
              renderReviewsSection('reviews-section--mobile-accordion', {
                mobileAccordion: true,
                expanded: mobileReviewsExpanded,
                onToggle: () => setMobileReviewsExpanded((v) => !v),
              })}

            {/* Color & size on page (defaults hidden); mobile sheet reuses same selection state */}
            {renderProductColorSwatches()}
            {renderProductSizeSection()}

            {/* Stock note when no size row (default-only size, etc.) */}
            {!showSizeQtyRow && (
              <div className="quantity-section product-info-mobile-order--qty-only">
                {currentStock > 0 && (
                  <div className="stock-info">
                    <span className="stock-label">
                      <i className="fas fa-check-circle"></i> {renderStockAvailabilityNote(currentStock)}
                      {renderLowStockInlineQty(currentStock)}
                    </span>
                  </div>
                )}
                {currentStock === 0 &&
                  selectedColor &&
                  (selectedSize || !product.sizes || product.sizes.length === 0) && (
                    <div className="stock-info">
                      <span className="stock-label out-of-stock-label">
                        <i className="fas fa-times-circle"></i> Out of Stock
                      </span>
                    </div>
                  )}
              </div>
            )}

            {/* Validation Error */}
            {validationError && (
              <div className="validation-error product-info-mobile-order--validation">
                {validationError}
              </div>
            )}

            {/* Add to Bag & Direct Checkout (mobile: sticky bar + variant sheet) */}
            <div
              className={`purchase-actions product-info-mobile-order--purchase${isMobile ? ' purchase-actions--mobile-compact' : ''}`}
            >
              <button 
                type="button"
                className={`add-to-bag-btn purchase-actions__add-to-bag ${addingToBag ? 'adding' : ''} ${isProductCompletelyOutOfStock() ? 'out-of-stock' : ''} ${addToBagNeedsSelectionOnly ? 'add-to-bag-btn--needs-selection' : ''} ${addToBagDwellJiggle && !addingToBag && !isProductCompletelyOutOfStock() ? 'add-to-bag-btn--dwell-jiggle' : ''}`}
                onClick={() => (isMobile ? handleMobileStickyAddClick() : handleAddToBag(false))}
                disabled={addingToBag || isProductCompletelyOutOfStock()}
              >
                {addingToBag ? (
                  <>
                    <div className="btn-spinner"></div>
                    Adding to Bag...
                  </>
                ) : isProductCompletelyOutOfStock() ? (
                  'Out of Stock'
                ) : (
                  <>
                    <i className="fas fa-shopping-bag"></i> Add to Bag
                  </>
                )}
              </button>
              <button 
                type="button"
                className={`direct-checkout-btn ${addingToBag ? 'adding' : ''} ${isProductCompletelyOutOfStock() ? 'out-of-stock' : ''} ${addToBagNeedsSelectionOnly ? 'add-to-bag-btn--needs-selection' : ''}`}
                onClick={() => handleAddToBag(true)}
                disabled={addingToBag || isProductCompletelyOutOfStock()}
                title="Add to bag and proceed to checkout"
              >
                {addingToBag ? (
                  <>
                    <div className="btn-spinner"></div>
                    ...
                  </>
                ) : isProductCompletelyOutOfStock() ? (
                  'Out of Stock'
                ) : (
                  <>
                    <i className="fas fa-credit-card"></i> Direct Checkout
                  </>
                )}
              </button>

              {/* WhatsApp Order Button */}
              <button 
                className="whatsapp-order-btn"
                onClick={handleWhatsAppOrder}
                title="Order via WhatsApp"
              >
                <span className="whatsapp-icon">📱</span>
                Order via WhatsApp
              </button>
            </div>

            {/* Container 4: Desktop — Category / Brand / fabric / care, then Created by + Reviews in sidebar */}
            {!isMobile && (
              <>
                <div className="product-details-info">
                  <div className="detail-item detail-item--categories">
                    <strong>Category:</strong>
                    {renderProductCategoryLinks(false)}
                  </div>
                  {product.brand && (
                    <div className="detail-item">
                      <strong>Brand:</strong>
                      <button
                        type="button"
                        className="brand-link-btn"
                        onClick={() => {
                          const brandSlug = String(product.brand).toLowerCase().replace(/\s+/g, '-');
                          navigate(`/brand/${brandSlug}`);
                        }}
                        title={`View all ${product.brand} products`}
                      >
                        <i className="fas fa-copyright" style={{ marginRight: '4px', fontSize: '10px' }}></i>
                        {product.brand}
                      </button>
                    </div>
                  )}
                  {product.fabric && (
                    <div className="detail-item">
                      <strong>Fabric:</strong> {product.fabric}
                    </div>
                  )}
                  {product.care && (
                    <div className="detail-item">
                      <strong>Care Instructions:</strong> {product.care}
                    </div>
                  )}
                </div>
                {renderCreatorBlock('product-creator--desktop-sidebar')}
                {renderReviewsSection('reviews-section--desktop-sidebar')}
              </>
            )}
          </div>
      </div>
        
        {/* Container 6: Professional You May Also Like Section */}
        {relatedProducts.length > 0 && (
          <div
            className={`professional-related-section ${
              isMobile ? 'professional-related-section--mobile' : 'professional-related-section--desktop'
            }`}
          >
            <div
              className={`professional-container ${
                isMobile ? 'professional-container--mobile' : 'professional-container--desktop'
              }`}
            >
              <div className="section-title-wrapper">
                <h2 className="professional-title">You May Also Like</h2>
                <div className="title-underline" />
              </div>

              <div
                key={isMobile ? 'ymal-mobile' : `ymal-desktop-${ymalDesktopPage}`}
                className={`related-products-grid ${
                  isMobile ? 'related-products-grid--ymal-mobile' : 'related-products-grid--ymal-desktop'
                }`}
              >
                {displayRelatedProducts.map((product, idx) => {
                  if (!product || typeof product !== 'object' || !isCatalogProductForDisplay(product)) {
                    return null;
                  }
                  
                  // ✅ Extract category as string (handle both object and string)
                  const categoryValue = product.category;
                  const categoryString = typeof categoryValue === 'string' 
                    ? categoryValue 
                    : (categoryValue?.name || categoryValue?.slug || categoryValue?.fullPath || 'general');
                  
                  // ✅ Prepare product data for ProductCard component
                  const productForCard = {
                    id: product.id || product._id,
                    _id: product._id || product.id,
                    name: product.name || 'Product',
                    price: product.price || 0,
                    originalPrice: product.originalPrice || product.original_price || null,
                    salePrice: product.salePrice || product.sale_price || null,
                    images: product.images || (product.image ? [product.image] : []),
                    image: product.image || (product.images && product.images.length > 0 ? product.images[0] : null),
                    url: product.url || `product/${product.id || product._id}`,
                    isNew: product.isNew || product.is_new || false,
                    isOnSale: product.isOnSale || product.is_on_sale || false,
                    isFeatured: product.isFeatured || product.is_featured || false,
                    category: categoryString,
                    colors: product.colors || [],
                    sizes: product.sizes || [],
                    stock: product.stock || {},
                    description: product.description || '',
                    brand: product.brand || ''
                  };
                  
                  return (
                    <ProductCard
                      key={`ymal-${isMobile ? 'm' : `d${ymalDesktopPage}`}-${idx}-${productForCard.id || productForCard._id || idx}`}
                      product={productForCard}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!isMobile && (
          <FeaturedProductsCarousel
            excludeProductId={productPageId}
            className="featured-products--pdp-desktop"
          />
        )}

        {isMobile && (
          <FeaturedProductsCarousel
            excludeProductId={productPageId}
            className="featured-products--pdp-mobile"
          />
        )}

        {/* Image lightbox — mobile: dedicated full-screen gallery; desktop: modal */}
        {isModalOpen &&
          isMobile &&
          typeof document !== 'undefined' &&
          createPortal(
            <PdpMobileImageLightbox
              productName={product.name}
              slideCount={getGallerySlideCount()}
              currentIndex={modalImageIndex}
              onClose={closeImageModal}
              onSelectIndex={goToModalImage}
              getImageUrl={getGalleryUrlAt}
            />,
            document.body
          )}
        {isModalOpen && !isMobile && (
          <div
            className="image-modal-overlay"
            onClick={closeImageModal}
            role="dialog"
            aria-modal="true"
            aria-label={`${product.name} image viewer`}
          >
            <button
              type="button"
              className="modal-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                closeImageModal();
              }}
              aria-label="Close full screen image"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
            <div className="image-modal" onClick={(e) => e.stopPropagation()}>
              {getGallerySlideCount() > 1 && (
                <>
                  <button
                    type="button"
                    className="modal-nav-btn modal-prev"
                    onClick={prevModalImage}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="modal-nav-btn modal-next"
                    onClick={nextModalImage}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </>
              )}
              <div className="modal-image-container">
                <img
                  key={`modal-${modalImageIndex}`}
                  src={
                    getGalleryUrlAt(modalImageIndex) ||
                    getGalleryUrlAt(0) ||
                    undefined
                  }
                  alt={`${product.name} - View ${modalImageIndex + 1}`}
                  className={`modal-main-image ${isZoomed ? 'zoomed' : ''}`}
                  onClick={toggleZoom}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  style={{
                    cursor: isZoomed ? 'zoom-out' : 'zoom-in',
                    transformOrigin: isZoomed
                      ? `${zoomPosition.x}% ${zoomPosition.y}%`
                      : 'center',
                  }}
                />
                {isZoomed && (
                  <div className="zoom-overlay">
                    <span className="zoom-hint">Move cursor to explore • Click to zoom out</span>
                  </div>
                )}
              </div>
              {getGallerySlideCount() > 1 && (
                <div className={`modal-thumbnails-bottom ${isZoomed ? 'faded' : ''}`}>
                  {Array.from({ length: getGallerySlideCount() }, (_, index) => {
                    const thumbSrc = getGalleryUrlAt(index);
                    if (!thumbSrc) return null;
                    return (
                      <img
                        key={index}
                        src={thumbSrc}
                        alt={`Thumbnail ${index + 1}`}
                        className={`modal-thumbnail ${index === modalImageIndex ? 'active' : ''}`}
                        onClick={() => goToModalImage(index)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  })}
                </div>
              )}
              <div className={`modal-image-counter ${isZoomed ? 'faded' : ''}`}>
                {modalImageIndex + 1} / {getGallerySlideCount()}
              </div>
            </div>
          </div>
        )}
        
        {/* Size Chart Modal */}
        <SizeChart 
          category={safeRender(product.category, 'general')}
          isOpen={showSizeChart}
          onClose={() => setShowSizeChart(false)}
        />

        {isMobile &&
          typeof document !== 'undefined' &&
          createPortal(
            <>
              <div
                className={`pdp-mobile-sticky-bar${mobileVariantSheetOpen ? ' pdp-mobile-sticky-bar--sheet-open' : ''}`}
              >
                <button
                  type="button"
                  className={`add-to-bag-btn pdp-mobile-sticky-bar__btn ${addingToBag ? 'adding' : ''} ${isProductCompletelyOutOfStock() ? 'out-of-stock' : ''}`}
                  onClick={handleMobileStickyAddClick}
                  disabled={addingToBag || isProductCompletelyOutOfStock()}
                >
                  {addingToBag ? (
                    <>
                      <div className="btn-spinner"></div>
                      Adding...
                    </>
                  ) : isProductCompletelyOutOfStock() ? (
                    'Out of Stock'
                  ) : (
                    <>
                      <i className="fas fa-shopping-bag" aria-hidden /> Add to Bag
                    </>
                  )}
                </button>
              </div>

              {mobileVariantSheetOpen && (
                <div
                  className="pdp-mobile-variant-sheet"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Select color and size"
                >
                  <button
                    type="button"
                    className="pdp-mobile-variant-sheet__backdrop"
                    aria-label="Close"
                    onClick={() => {
                      setMobileVariantSheetOpen(false);
                      setValidationError('');
                    }}
                  />
                  <div className="pdp-mobile-variant-sheet__panel">
                    <div className="pdp-mobile-variant-sheet__header">
                      <h3 className="pdp-mobile-variant-sheet__title">{product.name}</h3>
                      <button
                        type="button"
                        className="pdp-mobile-variant-sheet__close"
                        aria-label="Close"
                        onClick={() => {
                          setMobileVariantSheetOpen(false);
                          setValidationError('');
                        }}
                      >
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    </div>
                    <div className="pdp-mobile-variant-sheet__body">
                      {showQuantityOnlyMode && (
                        <p className="pdp-mobile-variant-sheet__hint">Confirm to add this item to your bag.</p>
                      )}
                      {renderProductColorSwatches('pdp-mobile-variant-sheet__section')}
                      {renderProductSizeSection('pdp-mobile-variant-sheet__section')}
                      {validationError && (
                        <div className="validation-error pdp-mobile-variant-sheet__validation">
                          {validationError}
                        </div>
                      )}
                    </div>
                    <div className="pdp-mobile-variant-sheet__footer">
                      <button
                        type="button"
                        className={`add-to-bag-btn ${addingToBag ? 'adding' : ''} ${isProductCompletelyOutOfStock() ? 'out-of-stock' : ''}`}
                        onClick={handleMobileSheetConfirmAdd}
                        disabled={addingToBag || isProductCompletelyOutOfStock()}
                      >
                        {addingToBag ? (
                          <>
                            <div className="btn-spinner"></div>
                            Adding to Bag...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-shopping-bag" aria-hidden /> Add to Bag
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>,
            document.body
          )}
    </div>
  );
};

export default ProductPage; 